import { GenerationDayPreview } from './GenerationDayPreview';

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Button as MuiButton, Paper } from '@mui/material';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
  copyManualMealPlanDay,
  finalizeDraft,
  shuffleDraft,
  smartEditDraft,
  selectDraftRecipe,
  deselectDraftRecipe,
  updateFinalizedMealPlanItem,
  updateGenericMealPlanItem,
  updateMealPlanNotes,
  reorderMealPlanMeals,
  getDraftById,
  getDraftGenerationProgress,
  resumeMealPlanGeneration,
  getDraftShuffleStatus,
  getDraftSmartEditStatus,
  getMealPlanDraftByIdForAIDisplay,
  type UpdateMealPlanNotesRequest,
} from '@/lib/api/mealPlans';
import {
  extractErrorMessage,
  showErrorToast,
  showNotesSavedToast,
  showSuccessToast,
  showWarningToast,
} from '@/utils/toast-utils';
import { getClientById } from '@/lib/api/clients';
import {
  trackMealPlanDayCopyClicked,
  trackMealPlanFinalizeClicked,
} from '@/lib/analytics/trackers/mealPlanActions';
import { Client } from '@/types/client';
import { AlternateRecipe, AlternateRecipeResponse } from '@/types/alternate-recipe';
import MealPlanDraftHeader from '@/components/meal-planning/ai/MealPlanDraftHeader';
import MealPlanDaysTabs from '@/components/meal-planning/ai/MealPlanDaysTabs';
import { AI_MEAL_PLAN_MAX_DAYS } from '@/components/meal-planning/MealPlanScheduleFields';
import MealPlanEmptyState from '@/components/meal-planning/ai/MealPlanEmptyState';
import { MealSuggestionsClientSummaryStrip } from '@/components/meal-planning/manual/MealSuggestionsClientSummaryStrip';
import { ClientNameLink } from '@/components/clients/ClientNameLink';
import { MealGenerationShowcase } from '@/components/meal-planning/MealGenerationShowcase';
import SmartEditModal, {
  SmartEditMealContext,
  SmartEditSuggestion,
} from '@/components/meal-planning/ai/SmartEditModal';
import { useMealPlanData } from '@/hooks/useMealPlanData';
import { getAlternateMacroValue } from '@/utils/alternate-recipe-utils';
import type {
  DraftGenerationBlueprintSelection,
  FinalizedMeal,
  FinalizedMealPlanData,
} from '@/types/meal-plan';
import { getValidCuisineTypes } from '@/lib/clientPreferenceOptions';

type DraftRouteState = {
  planName?: string;
  initialStatus?: FinalizedMealPlanData['generationState'] | string;
  from?: string;
  requestedDietaryContext?: {
    allergies?: string[];
    intolerances?: string[];
    cuisineTypes?: string[];
    preferences?: string[];
    dietaryPreferences?: string[];
  };
};

type AiActionType = 'smart_edit' | 'regenerate';

type AiActionUsageState = {
  attemptsLeft?: number;
  attemptsUsed?: number;
  dailyLimit?: number;
  windowHours?: number;
  windowStartedAt?: string | null;
  resetAt?: string | null;
  retryAfterTimestamp?: string | null;
  retryAfterSeconds?: number;
};

type AiActionUsagePayload = AiActionUsageState & {
  dayNumber?: number;
  action?: AiActionType;
};

type AiActionUsageByDay = Record<number, AiActionUsageState>;
type DraftShuffleTargetSnapshot = {
  dayNumber: number;
  mealOrder: number;
  action?: AiActionType;
};

const MEAL_PLAN_PROGRESS_TICK_MS = 1000;
const MEAL_PLAN_PROGRESS_PHASE_MS = 90_000;
/** Typical planner call; the bar creeps towards the planner's share of the total until the first step completes. */
const MEAL_PLAN_PLANNER_EXPECTED_MS = 120_000;
const MEAL_PLAN_PROGRESS_PHASES = [
  'Reading client profile…',
  'Picking recipes…',
  'Balancing nutrition…',
  'Almost done…',
] as const;
const DRAFT_ASYNC_STATUS_POLL_INTERVAL_MS = 5000;

const getMealPlanGenerationPhase = (elapsedMs: number): typeof MEAL_PLAN_PROGRESS_PHASES[number] => {
  const phaseIndex = Math.min(
    MEAL_PLAN_PROGRESS_PHASES.length - 1,
    Math.max(0, Math.floor(elapsedMs / MEAL_PLAN_PROGRESS_PHASE_MS))
  );
  return MEAL_PLAN_PROGRESS_PHASES[phaseIndex];
};

const toBoundedPercentage = (value: unknown): number | null => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.min(100, Math.max(0, numeric));
};

type DraftAsyncOperationState = 'queued' | 'in_progress' | 'completed' | 'failed';
type DraftAsyncStatusSnapshot = {
  shuffleState?: DraftAsyncOperationState;
  smartEditState?: DraftAsyncOperationState;
  isProcessing?: boolean;
  shuffleTarget?: DraftShuffleTargetSnapshot;
};

const toDisplayToken = (value: string): string => (
  value
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => {
      if (/^\d+$/.test(part)) return part;
      if (part.length <= 5 && part.toUpperCase() === part) return part;
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(' ')
);

const formatClientDisplayName = (value: string): string => (
  value
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) =>
      word
        .split('-')
        .map((part) => part ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : part)
        .join('-')
    )
    .join(' ')
);

const formatDisplayList = (values: string[], limit = values.length): string => (
  values
    .slice(0, limit)
    .map((value) => toDisplayToken(String(value).trim()))
    .filter(Boolean)
    .join(', ')
);

const normalizeDietaryLookupKey = (value: string): string => (
  value.trim().toLowerCase().replace(/[\s-]+/g, '_')
);

const CUISINE_TYPE_LOOKUP_KEYS = new Set(
  getValidCuisineTypes().flatMap((cuisineType) => [
    normalizeDietaryLookupKey(cuisineType.value),
    normalizeDietaryLookupKey(cuisineType.name),
  ])
);

const isCuisineTypeValue = (value: string): boolean => (
  CUISINE_TYPE_LOOKUP_KEYS.has(normalizeDietaryLookupKey(value))
);

const uniqueDisplayValues = (values: string[]): string[] => {
  const seen = new Set<string>();
  return values.filter((value) => {
    const key = normalizeDietaryLookupKey(value);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const isInternalDisplayTag = (value: string): boolean => (
  normalizeDietaryLookupKey(value) === 'internal'
);

const toTrimmedStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter((item) => item.length > 0);
};

const firstNonEmptyStringArray = (...candidates: unknown[]): string[] => {
  for (const candidate of candidates) {
    const normalized = toTrimmedStringArray(candidate);
    if (normalized.length > 0) return normalized;
  }
  return [];
};

const firstNonEmptyString = (...candidates: unknown[]): string | undefined => {
  for (const candidate of candidates) {
    if (typeof candidate !== 'string') continue;
    const trimmed = candidate.trim();
    if (trimmed) return trimmed;
  }
  return undefined;
};

const toRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
};

const normalizeDraftAsyncOperationState = (
  value: unknown
): DraftAsyncOperationState | undefined => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase();
  if (
    normalized === 'queued' ||
    normalized === 'in_progress' ||
    normalized === 'completed' ||
    normalized === 'failed'
  ) {
    return normalized;
  }
  return undefined;
};

const FINALIZED_MEAL_PLAN_STATUSES = new Set(['finalized', 'completed', 'published', 'active']);

const isFinalizedMealPlanStatus = (value: unknown): boolean => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return FINALIZED_MEAL_PLAN_STATUSES.has(normalized);
};

const extractMissingMealDayNumber = (error: unknown): number | null => {
  const message = extractErrorMessage(error);
  if (!/has no items|all meals must have at least one recipe or ingredient/i.test(message)) {
    return null;
  }

  const dayMatch = message.match(/\bday\s+(\d+)\b/i);
  if (!dayMatch) return null;

  const dayNumber = Number(dayMatch[1]);
  return Number.isFinite(dayNumber) && dayNumber > 0 ? dayNumber : null;
};

const buildMissingMealDayMessage = (dayNumber: number): string =>
  `Day ${dayNumber} has incomplete meal selections. Please go to the Day ${dayNumber} tab and complete the remaining meal slots.`;

const readDraftStatusState = (
  payload: unknown,
  keys: string[]
): DraftAsyncOperationState | undefined => {
  const payloadRecord = toRecord(payload);
  const nestedData = toRecord(payloadRecord?.data);
  const nestedNestedData = toRecord(nestedData?.data);
  const sources = [payloadRecord, nestedData, nestedNestedData].filter(
    (source): source is Record<string, unknown> => source !== null
  );

  for (const source of sources) {
    for (const key of keys) {
      const state = normalizeDraftAsyncOperationState(source[key]);
      if (state) {
        return state;
      }
    }
  }

  return undefined;
};

const readDraftStatusBoolean = (
  payload: unknown,
  keys: string[]
): boolean | undefined => {
  const payloadRecord = toRecord(payload);
  const nestedData = toRecord(payloadRecord?.data);
  const nestedNestedData = toRecord(nestedData?.data);
  const sources = [payloadRecord, nestedData, nestedNestedData].filter(
    (source): source is Record<string, unknown> => source !== null
  );

  for (const source of sources) {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'boolean') {
        return value;
      }
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true') return true;
        if (normalized === 'false') return false;
      }
    }
  }

  return undefined;
};

const getPayloadSources = (payload: unknown): Record<string, unknown>[] => {
  const payloadRecord = toRecord(payload);
  const nestedData = toRecord(payloadRecord?.data);
  const nestedNestedData = toRecord(nestedData?.data);
  return [payloadRecord, nestedData, nestedNestedData].filter(
    (source): source is Record<string, unknown> => source !== null
  );
};

const toOptionalPositiveInteger = (value: unknown): number | undefined => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return undefined;
  return parsed;
};

const normalizeAiActionType = (value: unknown): AiActionType | undefined => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'smart_edit' || normalized === 'smartedit') return 'smart_edit';
  if (normalized === 'regenerate' || normalized === 'shuffle') return 'regenerate';
  return undefined;
};

const readDraftShuffleTarget = (payload: unknown): DraftShuffleTargetSnapshot | undefined => {
  for (const source of getPayloadSources(payload)) {
    const target = toRecord(source.shuffleTarget) ?? toRecord(source.shuffle_target);
    const candidate = target ?? source;
    const dayNumber = toOptionalPositiveInteger(
      candidate.dayNumber ?? candidate.day_number ?? candidate.day
    );
    const mealOrder = toOptionalPositiveInteger(
      candidate.mealOrder ?? candidate.meal_order ?? candidate.sortOrder ?? candidate.slotOrder
    );
    if (!dayNumber || !mealOrder) continue;
    const action = normalizeAiActionType(candidate.action ?? source.action);

    return {
      dayNumber,
      mealOrder,
      ...(action ? { action } : {}),
    };
  }

  return undefined;
};

const isTerminalDraftAsyncState = (
  state: DraftAsyncOperationState | undefined
): boolean => state === 'completed' || state === 'failed';

type AiDraftMacroTotals = {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  sugar: number;
  sodium: number;
  cholesterol: number;
  saturatedFat: number;
};

const createEmptyAiDraftMacros = (): AiDraftMacroTotals => ({
  calories: 0,
  protein: 0,
  carbs: 0,
  fat: 0,
  fiber: 0,
  sugar: 0,
  sodium: 0,
  cholesterol: 0,
  saturatedFat: 0,
});

const readAiDraftMacro = (nutrition: Record<string, unknown> | null, ...keys: string[]): number => {
  if (!nutrition) return 0;
  for (const key of keys) {
    const value = Number(nutrition[key]);
    if (Number.isFinite(value)) {
      return value;
    }
  }
  return 0;
};

const getAiDraftMealNutritionRecord = (meal: FinalizedMeal): Record<string, unknown> | null => {
  const nutrition = meal.nutrition;
  if (!nutrition || typeof nutrition !== 'object') return null;
  const macros = (nutrition as { macros?: unknown }).macros;
  if (macros && typeof macros === 'object') {
    return macros as Record<string, unknown>;
  }
  return nutrition as Record<string, unknown>;
};

const mealHasOptionGroupChoices = (meal: FinalizedMeal): boolean => {
  const candidates = [
    ...(Array.isArray(meal.recipes) ? meal.recipes : []),
    ...(Array.isArray((meal as { items?: FinalizedMeal[] }).items) ? (meal as { items?: FinalizedMeal[] }).items ?? [] : []),
  ];

  return candidates.some((item) => firstNonEmptyString(item.optionGroupId) !== undefined);
};

const isAiDraftMealSelected = (meal: FinalizedMeal): boolean => {
  if (!meal || typeof meal !== 'object') return false;
  if (mealHasOptionGroupChoices(meal)) {
    return firstNonEmptyString(meal.selectedOptionGroupId) !== undefined;
  }

  return firstNonEmptyString(meal.recipeId, meal.recipeName) !== undefined;
};

type AiDraftMissingMealSlot = {
  day: number | null;
  mealName: string;
};

const formatAiDraftMealSlotName = (value: string): string => {
  const cleaned = value.replace(/[_-]+/g, ' ').trim();
  if (!cleaned) return 'Meal slot';
  return cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
};

const getAiDraftSelectionSummary = (
  days: Array<FinalizedMealPlanData['days'][number]> | unknown[]
): {
  totalMeals: number;
  selectedMeals: number;
  missingSlots: AiDraftMissingMealSlot[];
} => {
  let totalMeals = 0;
  let selectedMeals = 0;
  const missingSlots: AiDraftMissingMealSlot[] = [];

  days.forEach((day) => {
    const dayRecord = day && typeof day === 'object' ? day as Record<string, unknown> : {};
    const dayNumberValue = Number(dayRecord.day ?? dayRecord.dayNumber ?? dayRecord.day_number);
    const dayNumber = Number.isFinite(dayNumberValue) ? dayNumberValue : null;
    const mealsRecord = dayRecord.meals && typeof dayRecord.meals === 'object'
      ? dayRecord.meals as Record<string, unknown>
      : {};

    Object.entries(mealsRecord).forEach(([mealKey, meal]) => {
      if (!meal || typeof meal !== 'object') return;

      totalMeals += 1;
      if (isAiDraftMealSelected(meal as FinalizedMeal)) {
        selectedMeals += 1;
        return;
      }

      const mealRecord = meal as Record<string, unknown>;
      const mealName = firstNonEmptyString(
        mealRecord.mealName,
        mealRecord.name,
        mealRecord.mealType,
        mealKey,
      ) ?? 'Meal slot';
      missingSlots.push({
        day: dayNumber,
        mealName: formatAiDraftMealSlotName(mealName),
      });
    });
  });

  return {
    totalMeals,
    selectedMeals,
    missingSlots,
  };
};

const getAiDraftSelectedMealMacros = (meal: FinalizedMeal): AiDraftMacroTotals => {
  if (!isAiDraftMealSelected(meal)) {
    return createEmptyAiDraftMacros();
  }

  const nutrition = getAiDraftMealNutritionRecord(meal);
  return {
    calories: readAiDraftMacro(nutrition, 'calories'),
    protein: readAiDraftMacro(nutrition, 'protein'),
    carbs: readAiDraftMacro(nutrition, 'carbs', 'carbohydrate'),
    fat: readAiDraftMacro(nutrition, 'fat', 'total_fat'),
    fiber: readAiDraftMacro(nutrition, 'fiber', 'dietary_fibre'),
    sugar: readAiDraftMacro(nutrition, 'sugar'),
    sodium: readAiDraftMacro(nutrition, 'sodium'),
    cholesterol: readAiDraftMacro(nutrition, 'cholesterol'),
    saturatedFat: readAiDraftMacro(nutrition, 'saturatedFat', 'saturated_fat'),
  };
};

const addAiDraftMacros = (
  target: AiDraftMacroTotals,
  source: AiDraftMacroTotals
): AiDraftMacroTotals => {
  (Object.keys(target) as Array<keyof AiDraftMacroTotals>).forEach((key) => {
    target[key] += source[key];
  });
  return target;
};

const roundAiDraftMacro = (value: number): number => (
  Math.round((Number.isFinite(value) ? value : 0) * 100) / 100
);

const roundAiDraftMacros = (macros: AiDraftMacroTotals): AiDraftMacroTotals => {
  const rounded = createEmptyAiDraftMacros();
  (Object.keys(rounded) as Array<keyof AiDraftMacroTotals>).forEach((key) => {
    rounded[key] = roundAiDraftMacro(macros[key]);
  });
  return rounded;
};

const divideAiDraftMacros = (macros: AiDraftMacroTotals, divisor: number): AiDraftMacroTotals => {
  if (!Number.isFinite(divisor) || divisor <= 0) {
    return createEmptyAiDraftMacros();
  }

  const divided = createEmptyAiDraftMacros();
  (Object.keys(divided) as Array<keyof AiDraftMacroTotals>).forEach((key) => {
    divided[key] = macros[key] / divisor;
  });
  return roundAiDraftMacros(divided);
};

const buildAiDraftDayTotals = (
  existing: FinalizedMealPlanData['days'][number]['dayTotals'],
  macros: AiDraftMacroTotals
): FinalizedMealPlanData['days'][number]['dayTotals'] => ({
  macros: roundAiDraftMacros(macros),
  micronutrients: existing?.micronutrients ?? ({ vitamins: {}, minerals: {} } as FinalizedMealPlanData['days'][number]['dayTotals']['micronutrients']),
});

const withRecalculatedAiDraftNutrition = (
  plan: FinalizedMealPlanData
): FinalizedMealPlanData => {
  const overallMacros = createEmptyAiDraftMacros();
  let daysWithSelection = 0;

  const nextDays = plan.days.map((day) => {
    const dayMacros = createEmptyAiDraftMacros();
    let selectedMeals = 0;

    Object.values(day.meals || {}).forEach((meal) => {
      if (!meal || typeof meal !== 'object') return;
      if (isAiDraftMealSelected(meal)) {
        selectedMeals += 1;
      }
      addAiDraftMacros(dayMacros, getAiDraftSelectedMealMacros(meal));
    });

    if (selectedMeals > 0) {
      daysWithSelection += 1;
    }
    addAiDraftMacros(overallMacros, dayMacros);

    return {
      ...day,
      dayTotals: buildAiDraftDayTotals(day.dayTotals, dayMacros),
    };
  });

  const existingSummary = plan.nutritionSummary;
  const overallRounded = roundAiDraftMacros(overallMacros);
  const averageRounded = divideAiDraftMacros(overallMacros, daysWithSelection);

  return {
    ...plan,
    days: nextDays,
    nutritionSummary: {
      overall: {
        ...(existingSummary?.overall ?? { micronutrients: { vitamins: {}, minerals: {} } }),
        macros: overallRounded,
      },
      dailyAverages: {
        ...(existingSummary?.dailyAverages ?? { micronutrients: { vitamins: {}, minerals: {} } }),
        macros: averageRounded,
      },
    },
  };
};

const AIMealPlanDraftPage = () => {
  const { clientId, draftId } = useParams<{ clientId: string; draftId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [swappingMeal, setSwappingMeal] = useState<{ day: number; meal: string; mealOrder?: number } | null>(null);
  const [clientDetails, setClientDetails] = useState<Client | null>(null);
  const [loadingClientDetails, setLoadingClientDetails] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [generationProgressStartMs, setGenerationProgressStartMs] = useState<number | null>(null);
  const [generationProgressNowMs, setGenerationProgressNowMs] = useState(() => Date.now());
  const [generationProgressFromApi, setGenerationProgressFromApi] = useState<number | null>(null);
  const [generationPromptCounts, setGenerationPromptCounts] = useState<{ completed: number; total: number } | null>(null);
  const [generationBlueprint, setGenerationBlueprint] = useState<DraftGenerationBlueprintSelection | null>(null);
  const [progressGenerationState, setProgressGenerationState] = useState<
    'queued' | 'in_progress' | 'completed' | 'partial' | 'failed' | undefined
  >(undefined);
  const [isResumingGeneration, setIsResumingGeneration] = useState(false);
  const lastCompletedPromptsRef = useRef<number | null>(null);
  const [alternateRecipesMap, setAlternateRecipesMap] = useState<Record<string, AlternateRecipeResponse>>({});
  const [activeDayTab, setActiveDayTab] = useState<string>(() => {
    const dayFromSearch = new URLSearchParams(location.search).get('day')?.trim();
    return dayFromSearch || '1';
  });
  const [smartEditOpen, setSmartEditOpen] = useState(false);
  const [smartEditTarget, setSmartEditTarget] = useState<SmartEditMealContext | null>(null);
  const [smartEditPrompt, setSmartEditPrompt] = useState('');
  const [smartEditSuggestions, setSmartEditSuggestions] = useState<SmartEditSuggestion[]>([]);
  const [smartEditSelectedSuggestionId, setSmartEditSelectedSuggestionId] = useState<string | null>(null);
  const [smartEditProgressNotice, setSmartEditProgressNotice] = useState<string | null>(null);
  const [smartEditGenerating, setSmartEditGenerating] = useState(false);
  const [smartEditApplying, setSmartEditApplying] = useState(false);
  const [smartEditUsageByDay, setSmartEditUsageByDay] = useState<AiActionUsageByDay>({});
  const [regenerateUsageByDay, setRegenerateUsageByDay] = useState<AiActionUsageByDay>({});
  const [smartEditUsage, setSmartEditUsage] = useState<AiActionUsageState>({});
  const [regenerateUsage, setRegenerateUsage] = useState<AiActionUsageState>({});
  const smartEditPreviewRequestRef = useRef(0);
  const smartEditPollTimerRef = useRef<number | null>(null);
  const [asyncStatus, setAsyncStatus] = useState<DraftAsyncStatusSnapshot>({});
  const [asyncStatusPollTrigger, setAsyncStatusPollTrigger] = useState(0);
  const [isSavingMealPlanNotes, setIsSavingMealPlanNotes] = useState(false);
  const asyncStatusRef = useRef<DraftAsyncStatusSnapshot>({});

  const mergeAsyncStatus = useCallback((patch: DraftAsyncStatusSnapshot) => {
    const nextStatus = {
      ...asyncStatusRef.current,
      ...patch,
    };
    asyncStatusRef.current = nextStatus;
    setAsyncStatus(nextStatus);
  }, []);

  const requestAsyncStatusPoll = useCallback(() => {
    setAsyncStatusPollTrigger((version) => version + 1);
  }, []);

  const fetchClientDetails = useCallback(async (id: string) => {
    setLoadingClientDetails(true);
    try {
      const response = await getClientById(id);
      setClientDetails(response?.client ?? null);
    } catch (error) {
      console.error('Error fetching client details:', error);
      setClientDetails(null);
    } finally {
      setLoadingClientDetails(false);
    }
  }, []);

  const {
    data: mealPlanData,
    setData: setMealPlanData,
    loading,
    error: mealPlanError,
    refresh: refreshMealPlan,
  } = useMealPlanData(draftId, getMealPlanDraftByIdForAIDisplay);

  const routeState = (location.state as DraftRouteState | null) ?? null;
  const detailsV2MealPlanPath = clientId
    ? `/clients/${clientId}/details-v2/meal-plan?fromPage=1&fromLimit=10`
    : '/meal-planning';
  const detailsV2AiMealPlanPath = clientId
    ? `/clients/${clientId}/details-v2/meal-plan?fromPage=1&fromLimit=10&category=ai`
    : '/meal-planning';
  const initialGenerationStateFromRoute =
    routeState?.initialStatus === 'queued' ||
    routeState?.initialStatus === 'in_progress' ||
    routeState?.initialStatus === 'completed' ||
    routeState?.initialStatus === 'partial' ||
    routeState?.initialStatus === 'failed'
      ? routeState.initialStatus
      : undefined;

  const generationState =
    mealPlanData?.generationState ?? initialGenerationStateFromRoute ?? progressGenerationState;
  const generationError = mealPlanData?.generationError;
  const isGenerationPending = generationState === 'queued' || generationState === 'in_progress';
  const generatedPreviewDays = mealPlanData?.generationPreviewDays ?? [];
  const requestedGenerationDays = Number(mealPlanData?.searchParams?.days) || generatedPreviewDays.length;
  const missingGeneratedDays = Math.max(0, requestedGenerationDays - generatedPreviewDays.length);
  const emptyMealSlots = generatedPreviewDays.reduce(
    (count, day) => count + (day.meals ?? []).filter((meal) => !(meal.recipeSuggestions ?? []).length).length,
    0,
  );
  const handleResumeGeneration = useCallback(async () => {
    if (!draftId) return;
    setIsResumingGeneration(true);
    try {
      const response = await resumeMealPlanGeneration(draftId);
      if (!response?.success) throw new Error(response?.message || 'Unable to resume meal plan generation');
      lastCompletedPromptsRef.current = null;
      setProgressGenerationState('queued');
      // The reloaded draft reports "queued", which switches the page to its progress view and pollers.
      await Promise.resolve(refreshMealPlan({ suppressLoading: true }));
    } catch (error) {
      showErrorToast(error, 'Unable to finish the remaining meals');
    } finally {
      setIsResumingGeneration(false);
    }
  }, [draftId, refreshMealPlan]);
  const days = useMemo(() => {
    const rawDays = (mealPlanData as { days?: unknown } | null | undefined)?.days;
    return Array.isArray(rawDays) ? rawDays : [];
  }, [mealPlanData]);
  const aiDraftSelectionSummary = useMemo(() => getAiDraftSelectionSummary(days), [days]);
  const isGenericMealPlan = useMemo(() => {
    const planRecord = toRecord(mealPlanData);
    const genericTokens = [
      planRecord?.generationType,
      planRecord?.generationSource,
      planRecord?.creationMethod,
      planRecord?.planType,
      planRecord?.type,
      planRecord?.source,
    ]
      .map((value) => String(value ?? '').trim().toLowerCase())
      .filter(Boolean);

    if (genericTokens.some((value) => value.includes('generic'))) {
      return true;
    }

    return days.some((day) => {
      const dayRecord = toRecord(day);
      const mealsRecord = toRecord(dayRecord?.meals);
      if (!mealsRecord) return false;

      return Object.values(mealsRecord).some((meal) => {
        const mealRecord = toRecord(meal);
        if (!mealRecord) return false;
        if (firstNonEmptyString(mealRecord.selectedOptionGroupId, mealRecord.selected_option_group_id)) {
          return true;
        }
        const rows = [
          ...(Array.isArray(mealRecord.recipes) ? mealRecord.recipes : []),
          ...(Array.isArray(mealRecord.items) ? mealRecord.items : []),
          ...(Array.isArray(mealRecord.ingredients) ? mealRecord.ingredients : []),
        ];
        return rows.some((row) => {
          const rowRecord = toRecord(row);
          return Boolean(firstNonEmptyString(rowRecord?.optionGroupId, rowRecord?.option_group_id));
        });
      });
    });
  }, [days, mealPlanData]);
  const dayTabValues = useMemo(() => days.map((day) => String(day.day ?? 1)), [days]);

  useEffect(() => {
    const dayFromSearch = new URLSearchParams(location.search).get('day')?.trim();
    if (dayFromSearch) {
      setActiveDayTab(dayFromSearch);
    }
  }, [location.search]);

  useEffect(() => {
    if (dayTabValues.length === 0) return;
    setActiveDayTab((previous) => (dayTabValues.includes(previous) ? previous : dayTabValues[0]));
  }, [dayTabValues]);

  useEffect(() => {
    if (!isGenerationPending) {
      setGenerationProgressStartMs(null);
      setGenerationProgressFromApi(null);
      setGenerationPromptCounts(null);
      setGenerationBlueprint(null);
      return;
    }

    const startedAt = Date.now();
    setGenerationProgressStartMs(startedAt);
    setGenerationProgressNowMs(startedAt);
  }, [isGenerationPending]);

  useEffect(() => {
    if (!isGenerationPending || generationProgressStartMs === null) return;

    const intervalId = window.setInterval(() => {
      setGenerationProgressNowMs(Date.now());
    }, MEAL_PLAN_PROGRESS_TICK_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [generationProgressStartMs, isGenerationPending]);

  const generationProgressPhase = useMemo(() => {
    if (!isGenerationPending || generationProgressStartMs === null) return 0;

    const elapsedMs = Math.max(0, generationProgressNowMs - generationProgressStartMs);
    return getMealPlanGenerationPhase(elapsedMs);
  }, [generationProgressNowMs, generationProgressStartMs, isGenerationPending]);
  // Nothing counts as done until the planner returns, so the API reports 0% for the
  // first minutes. Show elapsed-time progress within the planner's share of the bar.
  const plannerShare = generationPromptCounts ? 100 / generationPromptCounts.total : 10;
  const plannerElapsedMs = generationProgressStartMs === null ? 0 : Math.max(0, generationProgressNowMs - generationProgressStartMs);
  const estimatedPlannerProgress = isGenerationPending && (generationPromptCounts?.completed ?? 0) === 0
    ? Math.min(plannerShare * 0.9, (plannerElapsedMs / MEAL_PLAN_PLANNER_EXPECTED_MS) * plannerShare)
    : 0;
  const generationProgressValue = Math.round(Math.max(generationProgressFromApi ?? 0, estimatedPlannerProgress));
  const generationProgressSummary =
    generationProgressPhase === 0 ? MEAL_PLAN_PROGRESS_PHASES[0] : generationProgressPhase;

  useEffect(() => {
    if (!draftId || !isGenerationPending) {
      return;
    }

    let isCancelled = false;

    const loadGenerationProgress = async () => {
      try {
        const response = await getDraftGenerationProgress(draftId);
        if (isCancelled) return;

        const progressPercentage = toBoundedPercentage(response?.data?.promptProgress?.percentage);
        if (progressPercentage !== null) {
          setGenerationProgressFromApi(progressPercentage);
        }
        const completed = Number(response?.data?.promptProgress?.completedPrompts);
        const total = Number(response?.data?.promptProgress?.totalPrompts);
        if (Number.isFinite(completed) && Number.isFinite(total) && total > 0) {
          setGenerationPromptCounts({ completed, total });
        }

        // The full draft is large (every recipe carries 50 nutrients). Reload it only when
        // generation actually advanced or finished, not on a fixed timer.
        const completedPrompts = Number(response?.data?.promptProgress?.completedPrompts);
        const polledTerminal = response?.data?.generationState && response.data.generationState !== 'queued'
          && response.data.generationState !== 'in_progress';
        if (polledTerminal || (Number.isFinite(completedPrompts) && completedPrompts !== lastCompletedPromptsRef.current)) {
          lastCompletedPromptsRef.current = Number.isFinite(completedPrompts) ? completedPrompts : lastCompletedPromptsRef.current;
          refreshMealPlan({ suppressLoading: true });
        }

        if (response?.data?.blueprintSelection?.days?.length) {
          setGenerationBlueprint(response.data.blueprintSelection);
        }

        const polledState = response?.data?.generationState;
        if (polledState) {
          setProgressGenerationState(polledState);
        }

        if (polledState === 'completed') {
          setGenerationProgressFromApi(100);
        }
      } catch (error) {
        console.warn('Unable to read draft generation progress:', error);
      }
    };

    void loadGenerationProgress();

    const intervalId = window.setInterval(() => {
      void loadGenerationProgress();
    }, DRAFT_ASYNC_STATUS_POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      window.clearInterval(intervalId);
    };
  }, [draftId, isGenerationPending, refreshMealPlan]);

  useEffect(() => {
    if (!draftId || !isGenerationPending) {
      return;
    }

    // Safety net only: progress changes normally trigger the reload above.
    const intervalId = window.setInterval(() => {
      refreshMealPlan({ suppressLoading: true });
    }, 30000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [draftId, isGenerationPending, refreshMealPlan]);

  useEffect(() => {
    asyncStatusRef.current = {};
    setAsyncStatus({});
    setProgressGenerationState(undefined);
    setGenerationBlueprint(null);
  }, [draftId]);

  useEffect(() => {
    if (!draftId) return;

    let isCancelled = false;
    let timeoutId: number | null = null;

    const pollAsyncStatuses = async () => {
      try {
        const previous = asyncStatusRef.current;
        const shouldPollShuffle = !isTerminalDraftAsyncState(previous.shuffleState);
        const shouldPollSmartEdit = !isTerminalDraftAsyncState(previous.smartEditState);

        if (!shouldPollShuffle && !shouldPollSmartEdit) {
          return;
        }

        const [shuffleStatusResult, smartEditStatusResult] = await Promise.allSettled([
          shouldPollShuffle ? getDraftShuffleStatus(draftId) : Promise.resolve(null),
          shouldPollSmartEdit ? getDraftSmartEditStatus(draftId) : Promise.resolve(null),
        ]);
        const shuffleStatus =
          shuffleStatusResult.status === 'fulfilled' ? shuffleStatusResult.value : null;
        const smartEditStatus =
          smartEditStatusResult.status === 'fulfilled' ? smartEditStatusResult.value : null;

        if (isCancelled) return;

        const shuffleStatusTarget = readDraftShuffleTarget(shuffleStatus);
        const smartEditStatusTarget = readDraftShuffleTarget(smartEditStatus);
        const smartEditStatusIsRegenerate = smartEditStatusTarget?.action === 'regenerate';
        const nextShuffleState = shouldPollShuffle
          ? readDraftStatusState(shuffleStatus, [
              'shuffleState',
              'shuffle_state',
              'state',
              'status',
            ]) ??
            readDraftStatusState(smartEditStatus, [
              'shuffleState',
              'shuffle_state',
            ])
          : previous.shuffleState;
        const nextSmartEditState = shouldPollSmartEdit
          ? smartEditStatusIsRegenerate
            ? 'completed'
            : readDraftStatusState(smartEditStatus, [
                'smartEditState',
                'smart_edit_state',
                'shuffleState',
                'shuffle_state',
                'state',
                'status',
              ])
          : previous.smartEditState;
        const nextIsProcessing =
          (shouldPollShuffle
            ? readDraftStatusBoolean(shuffleStatus, ['isProcessing', 'is_processing'])
            : undefined) ??
          (shouldPollSmartEdit
            ? readDraftStatusBoolean(smartEditStatus, ['isProcessing', 'is_processing'])
            : undefined) ??
          previous.isProcessing;
        const nextShuffleTarget = shouldPollShuffle
          ? shuffleStatusTarget ??
            smartEditStatusTarget ??
            previous.shuffleTarget
          : previous.shuffleTarget;

        const shouldRefreshDraft =
          (nextShuffleState === 'completed' && previous.shuffleState !== 'completed') ||
          (nextSmartEditState === 'completed' && previous.smartEditState !== 'completed');

        const nextStatus = {
          shuffleState: nextShuffleState ?? previous.shuffleState,
          smartEditState: nextSmartEditState ?? previous.smartEditState,
          isProcessing: nextIsProcessing,
          shuffleTarget: nextShuffleTarget,
        };
        asyncStatusRef.current = nextStatus;
        setAsyncStatus(nextStatus);

        if (shouldRefreshDraft) {
          await getDraftById(draftId);
          await refreshMealPlan({ suppressLoading: true });
        }
      } catch (error) {
        console.warn('Unable to read async draft status', error);
      } finally {
        if (!isCancelled) {
          const current = asyncStatusRef.current;
          const hasPendingStatusPolls =
            !isTerminalDraftAsyncState(current.shuffleState) ||
            !isTerminalDraftAsyncState(current.smartEditState);

          if (!hasPendingStatusPolls) {
            return;
          }

          timeoutId = window.setTimeout(() => {
            void pollAsyncStatuses();
          }, DRAFT_ASYNC_STATUS_POLL_INTERVAL_MS);
        }
      }
    };

    void pollAsyncStatuses();

    return () => {
      isCancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [asyncStatusPollTrigger, draftId, refreshMealPlan]);

  useEffect(() => {
    if (clientId) {
      fetchClientDetails(clientId);
    }
  }, [clientId, fetchClientDetails]);

  useEffect(() => {
    return () => {
      smartEditPreviewRequestRef.current += 1;
      if (smartEditPollTimerRef.current) {
        window.clearTimeout(smartEditPollTimerRef.current);
        smartEditPollTimerRef.current = null;
      }
    };
  }, []);

  const getMealKey = (day: number, mealName: string) => `${day}-${mealName}`;

  useEffect(() => {
    if (mealPlanError && !isGenerationPending) {
      console.error('Error fetching meal plan:', mealPlanError);
      showErrorToast(mealPlanError, 'Error');
    }
  }, [mealPlanError, isGenerationPending]);

  const fromFullName = String(clientDetails?.fullName ?? '').trim();
  const fromFirstLast = [clientDetails?.firstName, clientDetails?.lastName]
    .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
    .join(' ')
    .trim();
  const rawClientDisplayName = fromFullName || fromFirstLast;
  const clientDisplayName = rawClientDisplayName
    ? formatClientDisplayName(rawClientDisplayName)
    : 'your client';

  const routeDietaryContext = routeState?.requestedDietaryContext;
  const searchDietaryPreferences = mealPlanData?.searchParams?.dietaryPreferences as
    | {
      allergies?: unknown;
      intolerances?: unknown;
      cuisineTypes?: unknown;
      dietaryPreferences?: unknown;
      preferences?: unknown;
    }
    | undefined;
  const searchOverrideClientGoals = mealPlanData?.searchParams?.overrideClientGoals as
    | {
      allergies?: unknown;
      intolerances?: unknown;
      cuisineTypes?: unknown;
      preferences?: unknown;
      dietaryPreferences?: unknown;
    }
    | null
    | undefined;

  const effectiveDietaryContext = useMemo(() => ({
    preferences: firstNonEmptyStringArray(
      routeDietaryContext?.preferences,
      routeDietaryContext?.dietaryPreferences,
      searchOverrideClientGoals?.preferences,
      searchOverrideClientGoals?.dietaryPreferences,
      searchDietaryPreferences?.dietaryPreferences,
      searchDietaryPreferences?.preferences,
      clientDetails?.dietaryPreferences,
    ),
    allergies: firstNonEmptyStringArray(
      routeDietaryContext?.allergies,
      searchOverrideClientGoals?.allergies,
      searchDietaryPreferences?.allergies,
      clientDetails?.allergies,
    ),
    intolerances: firstNonEmptyStringArray(
      routeDietaryContext?.intolerances,
      searchOverrideClientGoals?.intolerances,
      searchDietaryPreferences?.intolerances,
      clientDetails?.foodIntolerances,
    ),
    cuisineTypes: firstNonEmptyStringArray(
      routeDietaryContext?.cuisineTypes,
      searchOverrideClientGoals?.cuisineTypes,
      searchDietaryPreferences?.cuisineTypes,
      clientDetails?.cuisineTypesPreferred,
    ),
  }), [
    routeDietaryContext?.preferences,
    routeDietaryContext?.dietaryPreferences,
    routeDietaryContext?.allergies,
    routeDietaryContext?.intolerances,
    routeDietaryContext?.cuisineTypes,
    searchOverrideClientGoals?.preferences,
    searchOverrideClientGoals?.dietaryPreferences,
    searchOverrideClientGoals?.allergies,
    searchOverrideClientGoals?.intolerances,
    searchOverrideClientGoals?.cuisineTypes,
    searchDietaryPreferences?.dietaryPreferences,
    searchDietaryPreferences?.preferences,
    searchDietaryPreferences?.allergies,
    searchDietaryPreferences?.intolerances,
    searchDietaryPreferences?.cuisineTypes,
    clientDetails?.dietaryPreferences,
    clientDetails?.allergies,
    clientDetails?.foodIntolerances,
    clientDetails?.cuisineTypesPreferred,
  ]);

  const draftActionDietaryContext = useMemo(() => ({
    allergies: effectiveDietaryContext.allergies,
    intolerances: effectiveDietaryContext.intolerances,
    cuisineTypes: effectiveDietaryContext.cuisineTypes,
    preferences: effectiveDietaryContext.preferences,
    dietaryPreferences: effectiveDietaryContext.preferences,
  }), [effectiveDietaryContext]);

  const displayCuisineTypes = useMemo(() => (
    uniqueDisplayValues([
      ...effectiveDietaryContext.cuisineTypes,
      ...effectiveDietaryContext.preferences.filter(isCuisineTypeValue),
    ])
  ), [effectiveDietaryContext.cuisineTypes, effectiveDietaryContext.preferences]);

  const displayPreferences = useMemo(() => (
    effectiveDietaryContext.preferences.filter((preference) => !isCuisineTypeValue(preference))
  ), [effectiveDietaryContext.preferences]);

  const clientHighlights = useMemo(() => {
    const highlights: string[] = [];
    const conditionText =
      typeof clientDetails?.medicalConditions === 'string'
        ? clientDetails.medicalConditions.trim()
        : '';
    const conditionValues = conditionText
      ? conditionText.split(',').map((part) => toDisplayToken(part.trim())).filter(Boolean)
      : [];

    if (conditionValues.length > 0) {
      highlights.push(`Medical conditions: ${conditionValues.join(', ')}`);
    }
    if (effectiveDietaryContext.allergies.length > 0) {
      highlights.push(`Allergies: ${formatDisplayList(effectiveDietaryContext.allergies)}`);
    }
    if (effectiveDietaryContext.intolerances.length > 0) {
      highlights.push(`Intolerances: ${formatDisplayList(effectiveDietaryContext.intolerances)}`);
    }
    if (displayCuisineTypes.length > 0) {
      highlights.push(`Cuisine Types: ${formatDisplayList(displayCuisineTypes)}`);
    }
    if (displayPreferences.length > 0) {
      highlights.push(`Preferences: ${formatDisplayList(displayPreferences, 2)}`);
    }

    return highlights.length > 0
      ? highlights.join(' · ')
      : 'Preparing clinically appropriate options based on profile and goals.';
  }, [clientDetails, displayCuisineTypes, displayPreferences, effectiveDietaryContext]);

  const renderLoadingStepCard = (
    title: string,
    description: string,
    activeStep: 1 | 2 | 3 | 4 = 2
  ) => (
    <Box data-demo-id="ai-plan-draft-page" sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
        }}
      >
        <MealPlanDraftHeader
          onBack={() => navigate(detailsV2AiMealPlanPath)}
          clientDisplayName={clientDisplayName}
          clientId={clientId}
          activeStep={activeStep}
        />

        <div className="p-4 sm:p-5 lg:px-7 lg:pt-5 lg:pb-7">
          <section className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-12 text-center">
            <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
            <p className="mx-auto mt-2 max-w-2xl text-sm text-slate-500">{description}</p>
          </section>
        </div>
      </Paper>
    </Box>
  );

  const renderGenerationStepCard = () => (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
        }}
      >
        <MealPlanDraftHeader
          onBack={() => navigate(detailsV2AiMealPlanPath)}
          clientDisplayName={clientDisplayName}
          clientId={clientId}
          activeStep={2}
        />

        <div className="flex min-h-[460px] flex-1 items-center justify-center px-4 py-6">
          <MealGenerationShowcase
            heading={
              <>
                Generating meals for{' '}
                <ClientNameLink
                  clientId={clientId}
                  className="text-white underline-offset-2 hover:text-white hover:underline focus-visible:text-white"
                >
                  {clientDisplayName}
                </ClientNameLink>
              </>
            }
            subheading={clientHighlights || undefined}
            blueprint={generationBlueprint}
            progress={generationProgressValue}
            phaseLabel={generationProgressSummary}
          />
        </div>
      </Paper>
    </Box>
  );

  const handleViewDetails = (
    recipeId: string,
    recipeName: string,
    day: number,
    mealName: string,
    source: string | undefined,
    mealOrder: number
  ) => {
    if (!draftId || !clientId) return;

    const searchParams = new URLSearchParams();
    searchParams.set('day', String(day));
    searchParams.set('mealName', mealName);
    searchParams.set('mealOrder', String(mealOrder));
    searchParams.set('recipeName', recipeName);
    const normalizedSource = typeof source === 'string' ? source.trim() : '';
    if (normalizedSource) {
      searchParams.set('source', normalizedSource);
    }

    navigate(
      `/ai-meal-plan/draft/${draftId}/client/${clientId}/recipe/${encodeURIComponent(recipeId)}?${searchParams.toString()}`
    );
  };

  const normalizeAlternateIngredients = (
    recipe: Record<string, unknown>
  ): AlternateRecipe['ingredients'] => {
    const customization = recipe.customization as
      | { finalIngredients?: unknown; ingredients?: unknown }
      | undefined;

    const ingredientCandidates: unknown[] = [];

    if (Array.isArray(customization?.finalIngredients)) {
      ingredientCandidates.push(...customization.finalIngredients);
    }
    if (Array.isArray(customization?.ingredients)) {
      ingredientCandidates.push(...customization.ingredients);
    }
    if (Array.isArray(recipe.ingredients)) {
      ingredientCandidates.push(...(recipe.ingredients as unknown[]));
    }
    if (Array.isArray(recipe.extendedIngredients)) {
      ingredientCandidates.push(...(recipe.extendedIngredients as unknown[]));
    }
    if (Array.isArray(recipe.ingredientLines)) {
      ingredientCandidates.push(...(recipe.ingredientLines as unknown[]));
    }
    if (Array.isArray(recipe.ingredientsText)) {
      ingredientCandidates.push(...(recipe.ingredientsText as unknown[]));
    }

    const normalized = ingredientCandidates
      .map((item) => {
        if (typeof item === 'string') {
          const text = item.trim();
          return text ? { text } : null;
        }

        if (!item || typeof item !== 'object') {
          return null;
        }

        const ingredient = item as Record<string, unknown>;
        const quantityRaw = ingredient.quantity ?? ingredient.amount;
        const quantity =
          typeof quantityRaw === 'number'
            ? quantityRaw
            : Number.isFinite(Number(quantityRaw))
              ? Number(quantityRaw)
              : undefined;

        const unit =
          typeof ingredient.unit === 'string'
            ? ingredient.unit
            : typeof ingredient.measure === 'string'
              ? ingredient.measure
              : undefined;

        const food =
          typeof ingredient.food === 'string'
            ? ingredient.food
            : typeof ingredient.name === 'string'
              ? ingredient.name
              : undefined;

        const textParts = [quantity, unit, food].filter(Boolean).join(' ').trim();
        const preparationNote =
          typeof ingredient.preparationNote === 'string'
            ? ingredient.preparationNote.trim()
            : '';
        const text =
          (typeof ingredient.text === 'string' && ingredient.text.trim()) ||
          (typeof ingredient.originalString === 'string' && ingredient.originalString.trim()) ||
          (typeof ingredient.original === 'string' && ingredient.original.trim()) ||
          [textParts, preparationNote].filter(Boolean).join(', ').trim();

        if (!text && !food && quantity === undefined) {
          return null;
        }

        return {
          text: text || undefined,
          name: typeof ingredient.name === 'string' ? ingredient.name : food,
          quantity,
          amount:
            typeof ingredient.amount === 'number'
              ? ingredient.amount
              : quantity,
          measure: typeof ingredient.measure === 'string' ? ingredient.measure : unit,
          unit,
          food,
          weight:
            typeof ingredient.weight === 'number'
              ? ingredient.weight
              : Number.isFinite(Number(ingredient.grams))
                ? Number(ingredient.grams)
                : undefined,
        };
      })
      .filter((value): value is Exclude<typeof value, null> => value !== null);

    return normalized.length > 0 ? normalized : undefined;
  };

  const normalizeAlternateAllergens = (recipeInput: unknown): string[] => {
    const recipe = recipeInput as Record<string, unknown>;
    return [
      ...toTrimmedStringArray(recipe.allergens),
      ...toTrimmedStringArray(recipe.allergenTagIds),
      ...toTrimmedStringArray(recipe.allergyTags),
      ...toTrimmedStringArray(recipe.cautions),
    ].filter((value, index, arr) => (
      !isInternalDisplayTag(value) &&
      arr.findIndex((candidate) => candidate.toLowerCase() === value.toLowerCase()) === index
    ));
  };

  const hasAlternateAllergenFields = (recipeInput: unknown): boolean => {
    if (!recipeInput || typeof recipeInput !== 'object') return false;
    const recipe = recipeInput as Record<string, unknown>;
    return (
      Array.isArray(recipe.allergens) ||
      Array.isArray(recipe.allergenTagIds) ||
      Array.isArray(recipe.allergyTags) ||
      Array.isArray(recipe.cautions)
    );
  };

  const mergeAlternateRecipeForSelection = (
    primary: AlternateRecipe,
    fallback: AlternateRecipe
  ): AlternateRecipe => {
    const primaryAllergens = normalizeAlternateAllergens(primary);
    const allergens = primaryAllergens.length > 0 || hasAlternateAllergenFields(primary)
      ? primaryAllergens
      : normalizeAlternateAllergens(fallback);

    return {
      ...fallback,
      ...primary,
      allergens,
    };
  };

  const normalizeAlternateRecipe = (recipeInput: unknown): AlternateRecipe => {
    const recipe = recipeInput as Record<string, unknown>;
    const normalizedIngredients = normalizeAlternateIngredients(recipe);
    const allergens = normalizeAlternateAllergens(recipe);

    return {
      ...(recipe as AlternateRecipe),
      ingredients: normalizedIngredients,
      allergens,
      cuisines: toTrimmedStringArray(recipe.cuisines ?? recipe.cuisineTypes ?? recipe.cuisineType),
      cuisineTypes: toTrimmedStringArray(recipe.cuisineTypes ?? recipe.cuisines ?? recipe.cuisineType),
      mealTypes: toTrimmedStringArray(recipe.mealTypes ?? recipe.mealType),
      nutrition: (recipe.nutrition as AlternateRecipe['nutrition']) ?? {
        calories: { quantity: Number(recipe.calories ?? 0) },
        macros: {
          protein: { quantity: Number(recipe.protein ?? 0), unit: 'g' },
          carbs: { quantity: Number(recipe.carbs ?? 0), unit: 'g' },
          fat: { quantity: Number(recipe.fat ?? 0), unit: 'g' },
          fiber: { quantity: Number(recipe.fiber ?? 0), unit: 'g' },
        },
      },
    };
  };

  const toAlternateRecipesData = (shuffleData: unknown): AlternateRecipeResponse => {
    const data = (shuffleData && typeof shuffleData === 'object'
      ? (shuffleData as Record<string, unknown>)
      : {}) as Record<string, unknown>;

    const existingAlternates = Array.isArray(data.alternateRecipes) ? data.alternateRecipes : null;
    if (existingAlternates) {
      return {
        ...data,
        alternateRecipes: existingAlternates.map((recipe: unknown) =>
          normalizeAlternateRecipe(recipe)
        ),
      };
    }

    const suggestions = Array.isArray(data.recipeSuggestions) ? data.recipeSuggestions : [];
    const alternateRecipes = suggestions.map((recipe: unknown) =>
      normalizeAlternateRecipe(recipe)
    );

    return {
      ...data,
      alternateRecipes,
      metadata: {
        aiProvider:
          (typeof data.source === 'string' ? data.source : undefined) ??
          (data.metadata as { aiProvider?: string } | undefined)?.aiProvider,
        generatedAt:
          (typeof data.generatedAt === 'string' ? data.generatedAt : undefined) ??
          (data.metadata as { generatedAt?: string } | undefined)?.generatedAt,
        count: alternateRecipes.length,
      },
    };
  };

  const handleSwapRecipe = async (
    day: number,
    mealName: string,
    mealOrder: number,
    recipeId?: string
  ) => {
    if (!draftId) return;
    if (!mealOrder || mealOrder < 1) {
      showWarningToast('Meal order missing', 'Unable to shuffle this meal because the meal order is missing.');
      return;
    }
    const dayUsage = getRegenerateUsageForDay(day);
    const dayLimitReached =
      typeof dayUsage.attemptsLeft === 'number' && dayUsage.attemptsLeft <= 0;
    if (dayLimitReached) {
      showWarningToast('Regenerate Limit Reached', buildAiActionLimitMessage('Regenerates', dayUsage));
      return;
    }

    setSwappingMeal({ day, meal: mealName, mealOrder });
    mergeAsyncStatus({
      shuffleState: 'in_progress',
      isProcessing: true,
      shuffleTarget: { dayNumber: day, mealOrder, action: 'regenerate' },
    });
    requestAsyncStatusPoll();
    try {
      const response = await shuffleDraft(
        draftId,
        day,
        mealOrder,
        1,
        recipeId,
        draftActionDietaryContext
      );
      syncAiActionUsage(response?.data, day, 'regenerate');
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to fetch alternate recipes');
      }
      const key = getMealKey(day, mealName);
      const alternateData = toAlternateRecipesData(response?.data);
      const alternateRecipes = alternateData?.alternateRecipes || [];
      const shuffleStateRaw =
        response?.data && typeof response.data === 'object'
          ? (() => {
              const payload = response.data as Record<string, unknown>;
              const nestedData =
                payload.data && typeof payload.data === 'object'
                  ? (payload.data as Record<string, unknown>)
                  : null;
              const source = nestedData ?? payload;
              const state = source.shuffleState ?? source.shuffle_state;
              return typeof state === 'string' ? state.toLowerCase() : undefined;
            })()
          : undefined;
      const isShuffleQueued =
        shuffleStateRaw === 'queued' || shuffleStateRaw === 'in_progress';
      const normalizedShuffleState = normalizeDraftAsyncOperationState(shuffleStateRaw);

      if (normalizedShuffleState) {
        mergeAsyncStatus({
          shuffleState: normalizedShuffleState,
          isProcessing: isShuffleQueued,
        });
      } else if (alternateRecipes.length > 0) {
        mergeAsyncStatus({ shuffleState: 'completed', isProcessing: false });
      }

      if (isShuffleQueued) {
        requestAsyncStatusPoll();
      }
      
      if (alternateRecipes.length > 0) {
        setAlternateRecipesMap((prev) => ({
          ...prev,
          [key]: alternateData,
        }));
      } else if (isShuffleQueued) {
        showSuccessToast(
          'Regeneration queued',
          'New meal options are being prepared. Please try again in a few seconds.'
        );
      } else {
        // Store empty response to show empty state message
        setAlternateRecipesMap((prev) => ({
          ...prev,
          [key]: alternateData || { alternateRecipes: [] },
        }));
        showWarningToast('No alternate recipes found', 'No alternative recipes are available for this meal. Please try a different meal or adjust your preferences.');
      }
    } catch (error) {
      syncAiActionUsage(
        (error as { response?: { data?: unknown } } | undefined)?.response?.data,
        day,
        'regenerate'
      );
      console.error('Failed to fetch alternate recipes:', error);
      mergeAsyncStatus({ shuffleState: 'failed', isProcessing: false });
      showErrorToast(error, 'Swap failed');
    } finally {
      setSwappingMeal(null);
    }
  };

  const toSafeNumber = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const toOptionalNumber = (value: unknown): number | undefined => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const toOptionalDayNumber = (value: unknown): number | undefined => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 1) {
      return undefined;
    }
    return Math.trunc(parsed);
  };

  const normalizeUsageAction = (value: unknown): AiActionType | undefined => {
    if (typeof value !== 'string') return undefined;
    const normalized = value.trim().toLowerCase().replace(/[\s-]+/g, '_');
    if (!normalized) return undefined;
    if (normalized.includes('regenerate') || normalized.includes('shuffle')) {
      return 'regenerate';
    }
    if (normalized.includes('smart') || normalized.includes('edit')) {
      return 'smart_edit';
    }
    return undefined;
  };

  const extractAiActionUsageFromPayload = (payload: unknown): AiActionUsagePayload => {
    if (!payload || typeof payload !== 'object') {
      return {};
    }

    const record = payload as Record<string, unknown>;
    const nestedData =
      record.data && typeof record.data === 'object'
        ? (record.data as Record<string, unknown>)
        : null;
    const source = nestedData ?? record;

    return {
      dayNumber: toOptionalDayNumber(source.dayNumber ?? source.day_number ?? source.day),
      attemptsLeft: toOptionalNumber(source.attemptsLeft ?? source.attempts_left),
      attemptsUsed: toOptionalNumber(source.attemptsUsed ?? source.attempts_used),
      dailyLimit: toOptionalNumber(source.dailyLimit ?? source.daily_limit),
      windowHours: toOptionalNumber(source.windowHours ?? source.window_hours),
      windowStartedAt:
        typeof (source.windowStartedAt ?? source.window_started_at) === 'string'
          ? String(source.windowStartedAt ?? source.window_started_at)
          : undefined,
      resetAt:
        typeof (source.resetAt ?? source.reset_at) === 'string'
          ? String(source.resetAt ?? source.reset_at)
          : undefined,
      retryAfterTimestamp:
        typeof (source.retryAfterTimestamp ?? source.retry_after_timestamp) === 'string'
          ? String(source.retryAfterTimestamp ?? source.retry_after_timestamp)
          : undefined,
      retryAfterSeconds: toOptionalNumber(source.retryAfterSeconds ?? source.retry_after_seconds),
      action: normalizeUsageAction(source.action),
    };
  };

  const mergeAiActionUsageState = (
    previous: AiActionUsageState,
    nextUsage: AiActionUsagePayload
  ): AiActionUsageState => ({
    attemptsLeft: nextUsage.attemptsLeft ?? previous.attemptsLeft,
    attemptsUsed: nextUsage.attemptsUsed ?? previous.attemptsUsed,
    dailyLimit: nextUsage.dailyLimit ?? previous.dailyLimit,
    windowHours: nextUsage.windowHours ?? previous.windowHours,
    windowStartedAt: nextUsage.windowStartedAt ?? previous.windowStartedAt,
    resetAt: nextUsage.resetAt ?? previous.resetAt,
    retryAfterTimestamp: nextUsage.retryAfterTimestamp ?? previous.retryAfterTimestamp,
    retryAfterSeconds: nextUsage.retryAfterSeconds ?? previous.retryAfterSeconds,
  });

  const syncAiActionUsage = (
    payload: unknown,
    fallbackDayNumber?: number,
    fallbackAction?: AiActionType
  ) => {
    const nextUsage = extractAiActionUsageFromPayload(payload);
    const resolvedDayNumber = nextUsage.dayNumber ?? toOptionalDayNumber(fallbackDayNumber);
    const resolvedAction = nextUsage.action ?? fallbackAction;
    const hasUsageFields =
      nextUsage.attemptsLeft != null ||
      nextUsage.attemptsUsed != null ||
      nextUsage.dailyLimit != null ||
      nextUsage.windowHours != null ||
      nextUsage.windowStartedAt != null ||
      nextUsage.resetAt != null ||
      nextUsage.retryAfterTimestamp != null ||
      nextUsage.retryAfterSeconds != null;
    if (
      !resolvedAction ||
      !hasUsageFields
    ) {
      return;
    }

    const updateUsageByDay =
      resolvedAction === 'regenerate' ? setRegenerateUsageByDay : setSmartEditUsageByDay;
    const updateGlobalUsage =
      resolvedAction === 'regenerate' ? setRegenerateUsage : setSmartEditUsage;

    updateGlobalUsage((previous) => mergeAiActionUsageState(previous, nextUsage));

    if (resolvedDayNumber == null) {
      return;
    }

    updateUsageByDay((previousByDay) => {
      const previous = previousByDay[resolvedDayNumber] ?? {};
      return {
        ...previousByDay,
        [resolvedDayNumber]: mergeAiActionUsageState(previous, nextUsage),
      };
    });
  };

  const getActionUsageForDay = (
    usageByDay: AiActionUsageByDay,
    globalUsage: AiActionUsageState,
    dayNumber: number | undefined
  ): AiActionUsageState => {
    const resolvedDayNumber = toOptionalDayNumber(dayNumber);
    const dayUsage = resolvedDayNumber == null ? {} : usageByDay[resolvedDayNumber] ?? {};
    return {
      attemptsLeft: globalUsage.attemptsLeft ?? dayUsage.attemptsLeft,
      attemptsUsed: globalUsage.attemptsUsed ?? dayUsage.attemptsUsed,
      dailyLimit: globalUsage.dailyLimit ?? dayUsage.dailyLimit,
      windowHours: globalUsage.windowHours ?? dayUsage.windowHours,
      windowStartedAt: globalUsage.windowStartedAt ?? dayUsage.windowStartedAt,
      resetAt: globalUsage.resetAt ?? dayUsage.resetAt,
      retryAfterTimestamp: globalUsage.retryAfterTimestamp ?? dayUsage.retryAfterTimestamp,
      retryAfterSeconds: globalUsage.retryAfterSeconds ?? dayUsage.retryAfterSeconds,
    };
  };

  const getSmartEditUsageForDay = (dayNumber: number | undefined): AiActionUsageState => {
    return getActionUsageForDay(smartEditUsageByDay, smartEditUsage, dayNumber);
  };

  const getRegenerateUsageForDay = (dayNumber: number | undefined): AiActionUsageState => {
    return getActionUsageForDay(regenerateUsageByDay, regenerateUsage, dayNumber);
  };

  const formatUsageRetryTimestamp = (value?: string | null): string | null => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const buildAiActionLimitMessage = (
    actionLabel: string,
    usage: AiActionUsageState,
  ): string => {
    const windowHours = typeof usage.windowHours === 'number' && usage.windowHours > 0
      ? usage.windowHours
      : 24;
    const limitMessage =
      typeof usage.dailyLimit === 'number' && usage.dailyLimit > 0
        ? `You've used all ${usage.dailyLimit} ${actionLabel} in the last ${windowHours} hours.`
        : `You've used all ${actionLabel} available in the last ${windowHours} hours.`;
    const retryAt = formatUsageRetryTimestamp(usage.retryAfterTimestamp ?? usage.resetAt);
    return retryAt ? `${limitMessage} Come back after ${retryAt}.` : limitMessage;
  };

  const dedupeAlternateRecipesById = (recipes: AlternateRecipe[]): AlternateRecipe[] => {
    const uniqueById = new Map<string, AlternateRecipe>();
    recipes.forEach((recipe) => {
      const id = String(recipe?.id ?? '').trim();
      if (!id) return;
      if (!uniqueById.has(id)) {
        uniqueById.set(id, recipe);
      }
    });
    return Array.from(uniqueById.values());
  };

  const extractSelectResponseAlternates = (
    payload: unknown,
    day: number,
    mealOrder: number
  ): AlternateRecipe[] => {
    if (!payload || typeof payload !== 'object') return [];
    const payloadRecord = payload as Record<string, unknown>;
    const maybeNested = payloadRecord.data;
    const sources = [
      payloadRecord,
      maybeNested && typeof maybeNested === 'object'
        ? (maybeNested as Record<string, unknown>)
        : null,
    ].filter((source): source is Record<string, unknown> => source !== null);

    for (const source of sources) {
      const daySuggestions = Array.isArray(source.daySuggestions)
        ? (source.daySuggestions as Array<Record<string, unknown>>)
        : [];
      if (daySuggestions.length > 0) {
        const dayRecord = daySuggestions.find((entry) => {
          const dayValue = entry?.dayNumber ?? entry?.day;
          return Number(dayValue) === day;
        });
        if (dayRecord) {
          const meals = Array.isArray(dayRecord.meals)
            ? (dayRecord.meals as Array<Record<string, unknown>>)
            : [];
          const mealRecord = meals.find((entry) => {
            const orderValue =
              entry?.mealOrder ??
              entry?.meal_order ??
              entry?.slotOrder ??
              entry?.sortOrder;
            return Number(orderValue) === mealOrder;
          });
          if (mealRecord) {
            const slotSuggestions = Array.isArray(mealRecord.recipeSuggestions)
              ? mealRecord.recipeSuggestions
              : Array.isArray(mealRecord.recipes)
                ? mealRecord.recipes
                : [];
            if (slotSuggestions.length > 0) {
              return dedupeAlternateRecipesById(
                slotSuggestions.map((recipe) => normalizeAlternateRecipe(recipe))
              );
            }
          }
        }
      }

      const directSuggestions = Array.isArray(source.recipeSuggestions)
        ? source.recipeSuggestions
        : [];
      if (directSuggestions.length > 0) {
        return dedupeAlternateRecipesById(
          directSuggestions.map((recipe) => normalizeAlternateRecipe(recipe))
        );
      }
    }

    return [];
  };

  const buildAlternateRecipeResponse = (
    payload: unknown,
    alternates: AlternateRecipe[]
  ): AlternateRecipeResponse => {
    const dataRecord =
      payload && typeof payload === 'object'
        ? (payload as Record<string, unknown>)
        : {};
    const metadataRecord =
      dataRecord.metadata && typeof dataRecord.metadata === 'object'
        ? (dataRecord.metadata as Record<string, unknown>)
        : {};

    return {
      ...dataRecord,
      alternateRecipes: alternates,
      metadata: {
        ...metadataRecord,
        count: alternates.length,
      },
    };
  };

  const applySelectedAlternateToMealPlan = useCallback((
    day: number,
    mealName: string,
    mealOrder: number,
    recipe: AlternateRecipe
  ) => {
    const recipeRecord = recipe as AlternateRecipe & { nutritionServings?: number };
    const recipeId = String(recipe.id ?? '').trim();
    if (!recipeId) return;

    const buildSelectedMeal = (currentMeal: FinalizedMeal): FinalizedMeal => {
      const calories = toSafeNumber(getAlternateMacroValue(recipe, 'calories'));
      const protein = toSafeNumber(getAlternateMacroValue(recipe, 'protein'));
      const carbs = toSafeNumber(getAlternateMacroValue(recipe, 'carbs'));
      const fat = toSafeNumber(getAlternateMacroValue(recipe, 'fat'));
      const fiber = toSafeNumber(getAlternateMacroValue(recipe, 'fiber'));
      const source = String(recipe.source ?? '').trim();
      const recipeSource: FinalizedMeal['recipeSource'] | undefined =
        source === 'spoonacular' || source === 'edamam' || source === 'internal'
        ? source
        : currentMeal.recipeSource;
      const selectedAllergens = normalizeAlternateAllergens(recipe);

      return {
        ...currentMeal,
        recipeId,
        recipeName: recipe.title || currentMeal.recipeName || 'AI Suggested Meal',
        recipeImage: recipe.image || currentMeal.recipeImage || '',
        ...(recipeSource ? { recipeSource } : {}),
        itemId: String(recipe.itemId ?? recipeId).trim(),
        itemType: recipe.itemType ?? currentMeal.itemType ?? 'recipe',
        optionGroupId: recipe.optionGroupId ?? currentMeal.optionGroupId ?? null,
        optionGroupLabel: recipe.optionGroupLabel ?? currentMeal.optionGroupLabel ?? null,
        selectedOptionGroupId: recipe.selectedOptionGroupId ?? currentMeal.selectedOptionGroupId ?? null,
        servings: recipe.servings ?? currentMeal.servings,
        nutritionServings: recipeRecord.nutritionServings ?? recipe.servings ?? currentMeal.nutritionServings,
        cuisines: recipe.cuisines ?? recipe.cuisineTypes ?? currentMeal.cuisines,
        cuisineTypes: recipe.cuisineTypes ?? recipe.cuisines ?? currentMeal.cuisineTypes,
        mealTypes: recipe.mealTypes ?? currentMeal.mealTypes,
        allergens: selectedAllergens,
        generatedVia: recipe.generatedVia ?? currentMeal.generatedVia,
        generatedFromRecipeId: recipe.generatedFromRecipeId ?? currentMeal.generatedFromRecipeId,
        generatedFromSourceId: recipe.generatedFromSourceId ?? currentMeal.generatedFromSourceId,
        generatedFromTitle: recipe.generatedFromTitle ?? currentMeal.generatedFromTitle,
        generationGroupId: recipe.generationGroupId ?? currentMeal.generationGroupId,
        generatedAt: recipe.generatedAt ?? currentMeal.generatedAt,
        generationIndicator: recipe.generationIndicator ?? currentMeal.generationIndicator,
        mealOrder,
        nutrition: {
          calories,
          protein,
          carbs,
          fat,
          fiber,
          macros: {
            calories,
            protein,
            carbs,
            fat,
            fiber,
          },
        },
      };
    };

    setMealPlanData((previous) => {
      if (!previous) return previous;
      let didUpdate = false;
      const nextDays = previous.days.map((dayEntry) => {
        if (Number(dayEntry.day) !== day) return dayEntry;

        const directMeal = dayEntry.meals[mealName];
        const fallbackEntry = Object.entries(dayEntry.meals).find(([, meal]) => {
          const order = Number(meal.mealOrder ?? meal.slotOrder);
          return Number.isFinite(order) && order === mealOrder;
        });
        const mealKey = directMeal ? mealName : fallbackEntry?.[0];
        const currentMeal = directMeal ?? fallbackEntry?.[1];
        if (!mealKey || !currentMeal) return dayEntry;

        const selectedMeal = buildSelectedMeal(currentMeal);
        const currentOptions = Array.isArray(currentMeal.recipes) ? currentMeal.recipes : [];
        const nextOption = buildSelectedMeal({
          ...currentMeal,
          recipes: undefined,
        });
        const hasExistingOption = currentOptions.some((option) => {
          const optionId = String(option.recipeId ?? option.itemId ?? '').trim();
          return optionId === recipeId;
        });
        const nextOptions = hasExistingOption
          ? currentOptions.map((option) => {
              const optionId = String(option.recipeId ?? option.itemId ?? '').trim();
              return optionId === recipeId ? { ...option, ...nextOption } : option;
            })
          : [...currentOptions, nextOption];

        didUpdate = true;
        return {
          ...dayEntry,
          meals: {
            ...dayEntry.meals,
            [mealKey]: {
              ...selectedMeal,
              recipes: nextOptions,
            },
          },
        };
      });

      if (!didUpdate) return previous;
      return withRecalculatedAiDraftNutrition({
        ...previous,
        days: nextDays,
        updatedAt: new Date().toISOString(),
      });
    });
  }, [setMealPlanData, toSafeNumber]);

  const applyClearedMealSelectionToMealPlan = useCallback((
    day: number,
    mealName: string,
    mealOrder: number
  ) => {
    setMealPlanData((previous) => {
      if (!previous) return previous;
      let didUpdate = false;
      const emptyMacros = createEmptyAiDraftMacros();
      const nextDays = previous.days.map((dayEntry) => {
        if (Number(dayEntry.day) !== day) return dayEntry;

        const directMeal = dayEntry.meals[mealName];
        const fallbackEntry = Object.entries(dayEntry.meals).find(([, meal]) => {
          const order = Number(meal.mealOrder ?? meal.slotOrder);
          return Number.isFinite(order) && order === mealOrder;
        });
        const mealKey = directMeal ? mealName : fallbackEntry?.[0];
        const currentMeal = directMeal ?? fallbackEntry?.[1];
        if (!mealKey || !currentMeal) return dayEntry;

        didUpdate = true;
        return {
          ...dayEntry,
          meals: {
            ...dayEntry.meals,
            [mealKey]: {
              ...currentMeal,
              recipeId: '',
              recipeName: '',
              recipeImage: '',
              itemId: undefined,
              selectedOptionGroupId: null,
              mealOrder,
              allergens: [],
              nutrition: {
                calories: 0,
                protein: 0,
                carbs: 0,
                fat: 0,
                fiber: 0,
                sodium: 0,
                sugar: 0,
                cholesterol: 0,
                macros: {
                  calories: emptyMacros.calories,
                  protein: emptyMacros.protein,
                  carbs: emptyMacros.carbs,
                  fat: emptyMacros.fat,
                  fiber: emptyMacros.fiber,
                  sodium: emptyMacros.sodium,
                  sugar: emptyMacros.sugar,
                  cholesterol: emptyMacros.cholesterol,
                },
              },
            },
          },
        };
      });

      if (!didUpdate) return previous;
      return withRecalculatedAiDraftNutrition({
        ...previous,
        days: nextDays,
        updatedAt: new Date().toISOString(),
      });
    });
  }, [setMealPlanData]);

  const resolveGenericSelectionItemId = useCallback((
    day: number,
    mealName: string,
    mealOrder: number
  ): string => {
    const dayEntry = mealPlanData?.days.find((entry) => Number(entry.day) === day);
    const directMeal = dayEntry?.meals?.[mealName];
    const fallbackMeal = dayEntry
      ? Object.values(dayEntry.meals || {}).find((meal) => {
          const order = Number(meal.mealOrder ?? meal.slotOrder);
          return Number.isFinite(order) && order === mealOrder;
        })
      : undefined;
    const meal = directMeal ?? fallbackMeal;
    if (!meal) return '';

    const selectedOptionGroupId = String(meal.selectedOptionGroupId ?? '').trim();
    const candidates = [
      meal,
      ...(Array.isArray(meal.recipes) ? meal.recipes : []),
      ...(Array.isArray((meal as { items?: FinalizedMeal[] }).items) ? (meal as { items?: FinalizedMeal[] }).items ?? [] : []),
    ];
    const candidateWithItemId = (candidate: FinalizedMeal | undefined): string => (
      String(candidate?.itemId ?? '').trim()
    );

    if (selectedOptionGroupId) {
      const selectedGroupItem = candidates.find((candidate) => (
        String(candidate?.optionGroupId ?? '').trim() === selectedOptionGroupId &&
        candidateWithItemId(candidate)
      ));
      const itemId = candidateWithItemId(selectedGroupItem);
      if (itemId) return itemId;
    }

    return candidateWithItemId(candidates.find((candidate) => candidateWithItemId(candidate)));
  }, [mealPlanData]);

  const handleClearMealSelection = useCallback(async (
    day: number,
    mealName: string,
    mealOrder: number
  ) => {
    if (!draftId) return;
    if (!mealOrder || mealOrder < 1) {
      showWarningToast('Meal order missing', 'Unable to clear this selection because the meal order is missing.');
      return;
    }

    try {
      if (isGenericMealPlan) {
        const itemId = resolveGenericSelectionItemId(day, mealName, mealOrder);
        if (!itemId) {
          throw new Error('Unable to find the selected meal item to clear');
        }

        const response = isFinalizedMealPlanStatus(mealPlanData?.status)
          ? await updateFinalizedMealPlanItem(draftId, {
              itemId,
              selectedOptionGroupId: null,
            })
          : await updateGenericMealPlanItem({
              genericPlanId: draftId,
              mealName,
              itemId,
              selectedOptionGroupId: null,
            });

        if (!response?.success) {
          throw new Error(response?.message || 'Failed to clear meal choice');
        }

        await refreshMealPlan({ suppressLoading: true });
        showSuccessToast('Selection cleared', 'No meal is selected for this slot.');
        return;
      }

      const response = await deselectDraftRecipe(draftId, day, mealOrder, {
        responseMode: 'selection',
      });

      if (!response?.success) {
        throw new Error(response?.message || 'Failed to clear recipe selection');
      }

      applyClearedMealSelectionToMealPlan(day, mealName, mealOrder);
      showSuccessToast('Selection cleared', 'No meal is selected for this slot.');
    } catch (error) {
      console.error('Failed to clear meal selection:', error);
      showErrorToast(error, 'Clear selection failed');
    }
  }, [
    applyClearedMealSelectionToMealPlan,
    draftId,
    isGenericMealPlan,
    mealPlanData?.status,
    refreshMealPlan,
    resolveGenericSelectionItemId,
  ]);

  const handleSelectAlternateRecipe = async (
    day: number,
    mealName: string,
    mealOrder: number,
    newRecipe: AlternateRecipe
  ) => {
    if (!draftId || !newRecipe?.id) return;
    if (!mealOrder || mealOrder < 1) {
      showWarningToast('Meal order missing', 'Unable to select this recipe because the meal order is missing.');
      return;
    }

    const mealKey = getMealKey(day, mealName);
    const optionGroupId = String(newRecipe.optionGroupId ?? '').trim();
    const itemId = String(newRecipe.itemId ?? newRecipe.id ?? '').trim();

    try {
      if (isGenericMealPlan && optionGroupId) {
        const response = isFinalizedMealPlanStatus(mealPlanData?.status)
          ? await updateFinalizedMealPlanItem(draftId, {
              itemId,
              selectedOptionGroupId: optionGroupId,
            })
          : await updateGenericMealPlanItem({
              genericPlanId: draftId,
              mealName,
              itemId,
              selectedOptionGroupId: optionGroupId,
            });

        if (!response?.success) {
          throw new Error(response?.message || 'Failed to select meal choice');
        }

        await refreshMealPlan({ suppressLoading: true });
        setAlternateRecipesMap((prev) => ({
          ...prev,
          [mealKey]: {
            ...(prev[mealKey] ?? {}),
            alternateRecipes: prev[mealKey]?.alternateRecipes ?? [],
          },
        }));
        showSuccessToast('Meal choice selected', 'Nutrition will use the selected meal choice.');
        return;
      }

      const response = await selectDraftRecipe(draftId, day, mealOrder, newRecipe.id, {
        responseMode: 'selection',
      });

      if (!response?.success) {
        throw new Error(response?.message || 'Failed to swap recipe');
      }

      const alternatesFromResponse = extractSelectResponseAlternates(response?.data, day, mealOrder);
      const selectedRecipeFromResponse = alternatesFromResponse.find(
        (recipe) => String(recipe.id ?? '').trim() === String(newRecipe.id ?? '').trim()
      );
      const selectedRecipe = selectedRecipeFromResponse
        ? mergeAlternateRecipeForSelection(selectedRecipeFromResponse, newRecipe)
        : newRecipe;
      applySelectedAlternateToMealPlan(day, mealName, mealOrder, selectedRecipe);

      setAlternateRecipesMap((prev) => {
        const existingAlternates = prev[mealKey]?.alternateRecipes ?? [];
        const alternatesWithSelectedRecipe = alternatesFromResponse.map((recipe) => (
          String(recipe.id ?? '').trim() === String(selectedRecipe.id ?? '').trim()
            ? mergeAlternateRecipeForSelection(recipe, selectedRecipe)
            : recipe
        ));
        const alternatesForState = alternatesFromResponse.length > 0
          ? alternatesWithSelectedRecipe
          : existingAlternates.length > 0
            ? existingAlternates
            : [selectedRecipe];
        return {
          ...prev,
          [mealKey]: buildAlternateRecipeResponse(response?.data, alternatesForState),
        };
      });

      showSuccessToast('Recipe selected', 'Selection updated for this meal.');
    } catch (error) {
      console.error('Failed to update meal selection:', error);
      showErrorToast(error, isGenericMealPlan && optionGroupId ? 'Meal choice selection failed' : 'Selection failed');
    }
  };

  const buildSmartEditSuggestionFromAlternate = (recipe: AlternateRecipe): SmartEditSuggestion => ({
    id: String(recipe.id ?? ''),
    title: recipe.title || 'AI Suggested Meal',
    image: recipe.image || '',
    calories: toSafeNumber(getAlternateMacroValue(recipe, 'calories')),
    protein: toSafeNumber(getAlternateMacroValue(recipe, 'protein')),
    carbs: toSafeNumber(getAlternateMacroValue(recipe, 'carbs')),
    fat: toSafeNumber(getAlternateMacroValue(recipe, 'fat')),
    fiber: toSafeNumber(getAlternateMacroValue(recipe, 'fiber')),
    tags: ['High Protein', 'Anti-inflammatory'],
    bestFor: 'Breakfast',
    alternateRecipe: recipe,
    dietLabels: recipe.dietLabels ?? [],
    cuisines: recipe.cuisines ?? recipe.cuisineTypes ?? [],
    cuisineTypes: recipe.cuisineTypes ?? recipe.cuisines ?? [],
    generatedVia: recipe.generatedVia,
    generationIndicator: recipe.generationIndicator,
    generatedFromTitle: recipe.generatedFromTitle,
  });

  const buildSmartEditSuggestionsFromAlternates = (
    alternates: AlternateRecipe[] | undefined
  ): SmartEditSuggestion[] => {
    if (!Array.isArray(alternates) || alternates.length === 0) {
      return [];
    }

    return dedupeAlternateRecipesById(alternates).map((recipe) =>
      buildSmartEditSuggestionFromAlternate(recipe)
    );
  };

  const isSmartEditGeneratedRecipe = (recipe: AlternateRecipe): boolean => {
    const generatedVia = String(recipe.generatedVia ?? recipe.generationIndicator?.type ?? '')
      .trim()
      .toLowerCase();
    return generatedVia === 'smart_edit' || generatedVia === 'smartedit';
  };

  const getSmartEditSourceRefs = (recipe: AlternateRecipe): string[] => {
    return [
      recipe.generatedFromRecipeId,
      recipe.generatedFromSourceId,
      recipe.generationIndicator?.generatedFromRecipeId,
      recipe.generationIndicator?.generatedFromSourceId,
    ]
      .map((value) => String(value ?? '').trim())
      .filter((value) => value.length > 0);
  };

  const filterSmartEditAlternatesForModal = (
    alternates: AlternateRecipe[],
    target: SmartEditMealContext
  ): AlternateRecipe[] => {
    const smartEditAlternates = alternates.filter(isSmartEditGeneratedRecipe);
    if (smartEditAlternates.length === 0) {
      return [];
    }

    const targetRecipeRef = String(target.currentRecipeId || target.recipeId || '').trim();
    const matchingTarget = targetRecipeRef
      ? smartEditAlternates.filter((recipe) =>
          getSmartEditSourceRefs(recipe).some((ref) => ref === targetRecipeRef)
        )
      : [];

    return matchingTarget.length > 0 ? matchingTarget : smartEditAlternates;
  };

  const pickSmartEditSuggestion = (
    suggestions: SmartEditSuggestion[],
    currentRecipeId: string
  ): SmartEditSuggestion | undefined => {
    if (!Array.isArray(suggestions) || suggestions.length === 0) {
      return undefined;
    }

    const normalizedCurrentRecipeId = String(currentRecipeId ?? '').trim();
    const differentCandidate = suggestions.find((candidate) => {
      const candidateId = String(candidate?.id ?? '').trim();
      if (!candidateId) return false;
      if (!normalizedCurrentRecipeId) return true;
      return candidateId !== normalizedCurrentRecipeId;
    });

    return differentCandidate ?? suggestions[0];
  };

  const promoteSelectedSmartEditSuggestionToTarget = () => {
    if (!smartEditTarget || smartEditSuggestions.length === 0) {
      return;
    }

    const selectedSuggestion =
      smartEditSuggestions.find((candidate) => candidate.id === smartEditSelectedSuggestionId) ??
      smartEditSuggestions[0];
    const nextRecipeId = String(
      selectedSuggestion.alternateRecipe?.id ?? selectedSuggestion.id ?? ''
    ).trim();

    if (!nextRecipeId) {
      return;
    }

    setSmartEditTarget({
      ...smartEditTarget,
      currentRecipeId: nextRecipeId,
      recipeId: nextRecipeId,
      recipeName:
        selectedSuggestion.title ||
        selectedSuggestion.alternateRecipe?.title ||
        smartEditTarget.recipeName,
      recipeImage:
        selectedSuggestion.image ||
        selectedSuggestion.alternateRecipe?.image ||
        smartEditTarget.recipeImage,
      source: String(selectedSuggestion.alternateRecipe?.source ?? smartEditTarget.source ?? '').trim(),
      calories: selectedSuggestion.calories,
      protein: selectedSuggestion.protein,
      carbs: selectedSuggestion.carbs,
      fat: selectedSuggestion.fat,
      fiber: selectedSuggestion.fiber,
      alternateRecipe: selectedSuggestion.alternateRecipe ?? smartEditTarget.alternateRecipe,
    });
  };

  const activeDayNumber = toOptionalDayNumber(activeDayTab);
  const activeDaySmartEditUsage = getSmartEditUsageForDay(activeDayNumber);
  const activeDaySmartEditAttemptsLeft = activeDaySmartEditUsage.attemptsLeft;
  const activeDaySmartEditDailyLimit = activeDaySmartEditUsage.dailyLimit;
  const activeDaySmartEditRetryAt =
    activeDaySmartEditUsage.retryAfterTimestamp ?? activeDaySmartEditUsage.resetAt ?? null;
  const activeDaySmartEditLimitReached =
    typeof activeDaySmartEditAttemptsLeft === 'number' && activeDaySmartEditAttemptsLeft <= 0;
  const activeDayRegenerateUsage = getRegenerateUsageForDay(activeDayNumber);
  const activeDayRegenerateAttemptsLeft = activeDayRegenerateUsage.attemptsLeft;
  const activeDayRegenerateLimitReached =
    typeof activeDayRegenerateAttemptsLeft === 'number' && activeDayRegenerateAttemptsLeft <= 0;
  const effectiveShuffleState = normalizeDraftAsyncOperationState(
    asyncStatus.shuffleState ?? mealPlanData?.shuffleState
  );
  const effectiveShuffleTarget =
    asyncStatus.shuffleTarget ?? readDraftShuffleTarget(mealPlanData);
  const effectiveSmartEditState = normalizeDraftAsyncOperationState(asyncStatus.smartEditState);
  const isShufflePreparingAlternates =
    effectiveShuffleState === 'queued' ||
    effectiveShuffleState === 'in_progress' ||
    (asyncStatus.isProcessing === true && effectiveShuffleState !== 'completed');
  const effectiveSwappingMeal =
    isShufflePreparingAlternates &&
    effectiveShuffleTarget &&
    effectiveShuffleTarget.action !== 'smart_edit'
      ? {
          day: effectiveShuffleTarget.dayNumber,
          meal: '',
          mealOrder: effectiveShuffleTarget.mealOrder,
        }
      : swappingMeal;
  const isSmartEditPreparingSuggestions =
    effectiveSmartEditState === 'queued' ||
    effectiveSmartEditState === 'in_progress';

  const smartEditModalDayNumber = smartEditTarget?.day ?? activeDayNumber;
  const smartEditModalUsage = getSmartEditUsageForDay(smartEditModalDayNumber);
  const smartEditAttemptsLeft = smartEditModalUsage.attemptsLeft;
  const smartEditAttemptsUsed = smartEditModalUsage.attemptsUsed;
  const smartEditDailyLimit = smartEditModalUsage.dailyLimit;
  const SMART_EDIT_IN_PROGRESS_NOTICE =
    'Smart Edit is still generating suggestions for this meal. Please wait a few seconds and try again.';
  const SHUFFLE_IN_PROGRESS_NOTICE =
    'Meal options are still being prepared. Please wait a few seconds and try again.';
  const isSmartEditInProgressMessage = (message: string): boolean => {
    const normalized = message.toLowerCase();
    return (
      normalized.includes('already in progress') ||
      (normalized.includes('in progress') && normalized.includes('recipe')) ||
      (normalized.includes('in progress') && normalized.includes('shuffle'))
    );
  };
  const isSmartEditDailyLimitMessage = (message: string): boolean => {
    const normalized = message.toLowerCase();
    return (
      normalized.includes('daily limit') ||
      (normalized.includes('limited to') && normalized.includes('attempt')) ||
      (normalized.includes('limited to') && normalized.includes('uses per')) ||
      (normalized.includes('attempt') && normalized.includes('reached the limit')) ||
      normalized.includes('ai_meal_plan_daily_limit_exceeded') ||
      normalized.includes('attempts left: 0')
    );
  };

  const handleOpenSmartEdit = (payload: SmartEditMealContext) => {
    const dayUsage = getSmartEditUsageForDay(payload.day);
    const dayLimitReached =
      typeof dayUsage.attemptsLeft === 'number' && dayUsage.attemptsLeft <= 0;
    if (dayLimitReached) {
      showWarningToast(
        'Smart Edit Limit Reached',
        buildAiActionLimitMessage('Smart Edits', dayUsage)
      );
      return;
    }
    setSmartEditTarget(payload);
    setSmartEditPrompt('');
    setSmartEditSuggestions([]);
    setSmartEditSelectedSuggestionId(null);
    setSmartEditProgressNotice(null);
    setSmartEditOpen(true);
  };

  const handleSmartEditOpenChange = (open: boolean) => {
    smartEditPreviewRequestRef.current += 1;
    if (smartEditPollTimerRef.current) {
      window.clearTimeout(smartEditPollTimerRef.current);
      smartEditPollTimerRef.current = null;
    }
    setSmartEditOpen(open);
    if (!open) {
      setSmartEditPrompt('');
      setSmartEditSuggestions([]);
      setSmartEditSelectedSuggestionId(null);
      setSmartEditProgressNotice(null);
      setSmartEditTarget(null);
      setSmartEditGenerating(false);
    }
  };

  const handleSmartEditPromptChange = (value: string) => {
    smartEditPreviewRequestRef.current += 1;
    if (smartEditPollTimerRef.current) {
      window.clearTimeout(smartEditPollTimerRef.current);
      smartEditPollTimerRef.current = null;
    }
    promoteSelectedSmartEditSuggestionToTarget();
    setSmartEditPrompt(value);
    setSmartEditGenerating(false);
    setSmartEditSuggestions([]);
    setSmartEditSelectedSuggestionId(null);
    setSmartEditProgressNotice(null);
  };

  const handleSmartEditPresetSelect = (value: string) => {
    smartEditPreviewRequestRef.current += 1;
    if (smartEditPollTimerRef.current) {
      window.clearTimeout(smartEditPollTimerRef.current);
      smartEditPollTimerRef.current = null;
    }
    promoteSelectedSmartEditSuggestionToTarget();
    setSmartEditPrompt(value);
    setSmartEditGenerating(false);
    setSmartEditSuggestions([]);
    setSmartEditSelectedSuggestionId(null);
    setSmartEditProgressNotice(null);
  };

  const scheduleSmartEditStatusPoll = () => {
    if (smartEditPollTimerRef.current) {
      window.clearTimeout(smartEditPollTimerRef.current);
      smartEditPollTimerRef.current = null;
    }

    smartEditPollTimerRef.current = window.setTimeout(() => {
      void (async () => {
        if (!smartEditOpen || !smartEditTarget || !draftId) return;

        let smartEditStatusResponse: Record<string, unknown> | null = null;
        try {
          smartEditStatusResponse = await getDraftSmartEditStatus(draftId) as Record<string, unknown>;
        } catch {
          setSmartEditProgressNotice(SMART_EDIT_IN_PROGRESS_NOTICE);
          scheduleSmartEditStatusPoll();
          return;
        }

        if (!smartEditOpen || !smartEditTarget || !smartEditStatusResponse) {
          return;
        }

        const statusTarget = readDraftShuffleTarget(smartEditStatusResponse);
        const statusState = readDraftStatusState(smartEditStatusResponse, [
          'shuffleState',
          'shuffle_state',
          'state',
          'status',
        ]);
        if (statusTarget?.action === 'regenerate') {
          const stillRegenerating =
            statusState === 'queued' ||
            statusState === 'in_progress' ||
            statusState === undefined;
          mergeAsyncStatus({
            ...(statusState ? { shuffleState: statusState } : {}),
            shuffleTarget: statusTarget,
            isProcessing: stillRegenerating,
          });
          setSmartEditProgressNotice(stillRegenerating ? SHUFFLE_IN_PROGRESS_NOTICE : null);
          if (stillRegenerating) {
            scheduleSmartEditStatusPoll();
          }
          return;
        }

        syncAiActionUsage(smartEditStatusResponse, smartEditTarget.day, 'smart_edit');
        const smartEditState = readDraftStatusState(smartEditStatusResponse, [
          'smartEditState',
          'smart_edit_state',
          'shuffleState',
          'shuffle_state',
          'state',
          'status',
        ]);
        const stillInProgress =
          smartEditState === 'queued' ||
          smartEditState === 'in_progress' ||
          smartEditState === undefined;
        if (smartEditState) {
          mergeAsyncStatus({
            smartEditState,
            isProcessing: stillInProgress,
          });
        }

        if (stillInProgress) {
          setSmartEditProgressNotice(SMART_EDIT_IN_PROGRESS_NOTICE);
          scheduleSmartEditStatusPoll();
          return;
        }

        if (smartEditState === 'failed') {
          setSmartEditProgressNotice(null);
          return;
        }

        let rawDraftResponse: { success: boolean; message?: string; data?: unknown } | null = null;
        try {
          rawDraftResponse = await getDraftById(draftId);
        } catch {
          setSmartEditProgressNotice(SMART_EDIT_IN_PROGRESS_NOTICE);
          scheduleSmartEditStatusPoll();
          return;
        }

        if (!smartEditOpen || !smartEditTarget || !rawDraftResponse) {
          return;
        }

        const refreshedPayload = rawDraftResponse.data ?? rawDraftResponse;
        syncAiActionUsage(refreshedPayload, smartEditTarget.day, 'smart_edit');
        const refreshedAlternates = extractSelectResponseAlternates(
          refreshedPayload,
          smartEditTarget.day,
          smartEditTarget.mealOrder
        );

        if (refreshedAlternates.length > 0) {
          const key = getMealKey(smartEditTarget.day, smartEditTarget.mealName);
          const alternateData = buildAlternateRecipeResponse(
            refreshedPayload,
            refreshedAlternates
          );

          setAlternateRecipesMap((prev) => ({
            ...prev,
            [key]: alternateData,
          }));

          await refreshMealPlan({ suppressLoading: true });

          const suggestionsForModal = buildSmartEditSuggestionsFromAlternates(
            filterSmartEditAlternatesForModal(refreshedAlternates, smartEditTarget)
          );
          const defaultSuggestion = pickSmartEditSuggestion(
            suggestionsForModal,
            smartEditTarget.currentRecipeId
          );
          setSmartEditProgressNotice(null);
          setSmartEditSuggestions(suggestionsForModal);
          setSmartEditSelectedSuggestionId(defaultSuggestion?.id ?? null);
          return;
        }

        setSmartEditProgressNotice(null);
      })();
    }, DRAFT_ASYNC_STATUS_POLL_INTERVAL_MS);
  };

  const handleSmartEditSendPrompt = async () => {
    if (smartEditPollTimerRef.current) {
      window.clearTimeout(smartEditPollTimerRef.current);
      smartEditPollTimerRef.current = null;
    }
    if (!smartEditOpen || !smartEditTarget || !draftId) return;
    const instruction = smartEditPrompt.trim();
    if (!instruction) {
      smartEditPreviewRequestRef.current += 1;
      setSmartEditGenerating(false);
      setSmartEditSuggestions([]);
      setSmartEditSelectedSuggestionId(null);
      setSmartEditProgressNotice(null);
      return;
    }
    if (isShufflePreparingAlternates) {
      setSmartEditProgressNotice(SHUFFLE_IN_PROGRESS_NOTICE);
      return;
    }

    const requestId = smartEditPreviewRequestRef.current + 1;
    smartEditPreviewRequestRef.current = requestId;
    mergeAsyncStatus({
      smartEditState: 'in_progress',
      isProcessing: true,
      shuffleTarget: {
        dayNumber: smartEditTarget.day,
        mealOrder: smartEditTarget.mealOrder,
        action: 'smart_edit',
      },
    });
    requestAsyncStatusPoll();
    setSmartEditGenerating(true);
    setSmartEditProgressNotice(null);

    try {
      const response = await smartEditDraft(
        draftId,
        smartEditTarget.day,
        smartEditTarget.mealOrder,
        instruction,
        smartEditTarget.currentRecipeId || smartEditTarget.recipeId,
        draftActionDietaryContext
      );

      if (smartEditPreviewRequestRef.current !== requestId) return;
      syncAiActionUsage(response?.data, smartEditTarget.day, 'smart_edit');

      if (!response?.success) {
        const responseMessage = String(response?.message ?? '').trim();
        if (responseMessage && isSmartEditInProgressMessage(responseMessage)) {
          setSmartEditProgressNotice(SMART_EDIT_IN_PROGRESS_NOTICE);
          scheduleSmartEditStatusPoll();
        } else if (responseMessage && isSmartEditDailyLimitMessage(responseMessage)) {
          setSmartEditProgressNotice(null);
          showWarningToast('Smart Edit Limit Reached', responseMessage);
        } else {
          setSmartEditProgressNotice(null);
        }
        setSmartEditSuggestions([]);
        setSmartEditSelectedSuggestionId(null);
        return;
      }

      const key = getMealKey(smartEditTarget.day, smartEditTarget.mealName);
      const alternatesFromResponse = extractSelectResponseAlternates(
        response?.data,
        smartEditTarget.day,
        smartEditTarget.mealOrder
      );
      const alternateData = buildAlternateRecipeResponse(response?.data, alternatesFromResponse);
      setAlternateRecipesMap((prev) => ({
        ...prev,
        [key]: alternateData,
      }));

      const suggestionsForModal = buildSmartEditSuggestionsFromAlternates(
        filterSmartEditAlternatesForModal(alternatesFromResponse, smartEditTarget)
      );
      const defaultSuggestion = pickSmartEditSuggestion(
        suggestionsForModal,
        smartEditTarget.currentRecipeId
      );
      if (suggestionsForModal.length === 0) {
        setSmartEditProgressNotice(SMART_EDIT_IN_PROGRESS_NOTICE);
        setSmartEditSuggestions([]);
        setSmartEditSelectedSuggestionId(null);
        scheduleSmartEditStatusPoll();
        return;
      }
      setSmartEditProgressNotice(null);
      mergeAsyncStatus({ smartEditState: 'completed', isProcessing: false });
      setSmartEditSuggestions(suggestionsForModal);
      setSmartEditSelectedSuggestionId(defaultSuggestion?.id ?? null);
      void refreshMealPlan({ suppressLoading: true });
    } catch (error) {
      if (smartEditPreviewRequestRef.current !== requestId) return;
      syncAiActionUsage(
        (error as { response?: { data?: unknown } } | undefined)?.response?.data,
        smartEditTarget.day,
        'smart_edit'
      );
      console.error('Failed to preview smart edit suggestion:', error);
      const errorMessage = extractErrorMessage(error);
      if (isSmartEditInProgressMessage(errorMessage)) {
        setSmartEditProgressNotice(SMART_EDIT_IN_PROGRESS_NOTICE);
        scheduleSmartEditStatusPoll();
      } else if (isSmartEditDailyLimitMessage(errorMessage)) {
        setSmartEditProgressNotice(null);
        showWarningToast('Smart Edit Limit Reached', errorMessage);
      } else {
        setSmartEditProgressNotice(null);
      }
      setSmartEditSuggestions([]);
      setSmartEditSelectedSuggestionId(null);
    } finally {
      if (smartEditPreviewRequestRef.current === requestId) {
        setSmartEditGenerating(false);
      }
    }
  };

  const handleApplySmartEditSuggestion = async (suggestionId: string) => {
    if (!smartEditTarget) return;
    if (!draftId) {
      showWarningToast('Missing draft context', 'Unable to apply Smart Edit without a draft.');
      return;
    }

    setSmartEditSelectedSuggestionId(suggestionId);

    const selectedSuggestion = smartEditSuggestions.find(
      (candidate) => candidate.id === suggestionId
    );
    const alternateToApply =
      selectedSuggestion?.alternateRecipe ?? smartEditTarget.alternateRecipe;

    if (!alternateToApply) {
      showWarningToast(
        'No suggestion available',
        'Unable to apply this meal right now.'
      );
      return;
    }

    setSmartEditApplying(true);
    try {
      await handleSelectAlternateRecipe(
        smartEditTarget.day,
        smartEditTarget.mealName,
        smartEditTarget.mealOrder,
        alternateToApply
      );

      handleSmartEditOpenChange(false);
    } catch (error) {
      console.error('Failed to apply smart edit suggestion:', error);
      showErrorToast(error, 'Smart edit failed');
    } finally {
      setSmartEditApplying(false);
    }
  };

  const copyDaysFromSource = async (
    sourceDay: number,
    targetDaysInput: number[],
    options: {
      mode?: 'next_day';
    } = {}
  ): Promise<number[]> => {
    if (!draftId) {
      throw new Error('Draft ID is missing');
    }

    const targetDays = Array.from(
      new Set(
        targetDaysInput
          .map((day) => Number(day))
          .filter((day): day is number => Number.isFinite(day) && day !== sourceDay)
      )
    ).sort((left, right) => left - right);

    if (options.mode !== 'next_day' && targetDays.length === 0) {
      showWarningToast('No target days', 'There are no valid destination days to copy meals into.');
      return [];
    }

    let response;
    if (options.mode === 'next_day') {
      response = await copyManualMealPlanDay({
        draftId,
        sourceDay,
        mode: 'next_day',
      });
    } else if (targetDays.length === 1) {
      response = await copyManualMealPlanDay({
        draftId,
        sourceDay,
        targetDay: targetDays[0],
      });
    } else {
      response = await copyManualMealPlanDay({
        draftId,
        sourceDay,
        targetDays,
      });
    }

    if (!response?.success) {
      throw new Error(response?.message || 'Failed to copy meals');
    }

    await refreshMealPlan({ suppressLoading: true });
    return targetDays;
  };

  const handleCopyExistingDays = async (sourceDay: number, targetDaysInput: number[]) => {
    trackMealPlanDayCopyClicked({
      product_area: 'meal_plans',
      meal_plan_surface: 'ai_draft',
      copy_day_action: 'copy_existing',
    });
    const copiedDays = await copyDaysFromSource(sourceDay, targetDaysInput);
    if (copiedDays.length === 0) return;
    showSuccessToast('Days copied', `Copied meals from Day ${sourceDay} to day(s) ${copiedDays.join(', ')}.`);
  };

  const handleDuplicateToNextDay = async (sourceDay: number) => {
    trackMealPlanDayCopyClicked({
      product_area: 'meal_plans',
      meal_plan_surface: 'ai_draft',
      copy_day_action: 'duplicate_to_next',
    });
    try {
      const orderedDays = days
        .map((day) => Number(day?.day))
        .filter((day): day is number => Number.isFinite(day))
        .sort((left, right) => left - right);

      const sourceIndex = orderedDays.findIndex((day) => day === sourceDay);
      const nextDay = sourceIndex >= 0 ? orderedDays[sourceIndex + 1] : undefined;

      if (!nextDay) {
        showWarningToast('No next day', `Day ${sourceDay} does not have a next day to duplicate into.`);
        return;
      }

      const copiedDays = await copyDaysFromSource(sourceDay, [nextDay], {
        mode: 'next_day',
      });
      if (copiedDays.length === 0) return;

      showSuccessToast('Day duplicated', `Copied meals from Day ${sourceDay} to Day ${nextDay}.`);
    } catch (error) {
      showErrorToast(error, 'Duplicate failed');
    }
  };

  const handleApplyToAllDays = async (sourceDay: number) => {
    trackMealPlanDayCopyClicked({
      product_area: 'meal_plans',
      meal_plan_surface: 'ai_draft',
      copy_day_action: 'apply_to_all',
    });
    const targetDays = days
      .map((day) => Number(day?.day))
      .filter((day): day is number => Number.isFinite(day) && day !== sourceDay);
    const copiedDays = await copyDaysFromSource(sourceDay, targetDays);
    if (copiedDays.length === 0) return;
    showSuccessToast('Applied to all days', `Copied meals from Day ${sourceDay} to all other days.`);
  };

  const handleAddCopiedDay = async (sourceDay: number) => {
    const currentDayNumbers = days
      .map((day) => Number(day?.day))
      .filter((day): day is number => Number.isInteger(day) && day > 0);
    const newDay = Math.max(0, ...currentDayNumbers) + 1;
    if (newDay > AI_MEAL_PLAN_MAX_DAYS) {
      showWarningToast(
        'Maximum reached',
        `AI meal plans can include up to ${AI_MEAL_PLAN_MAX_DAYS} days.`
      );
      return;
    }

    try {
      const copiedDays = await copyDaysFromSource(sourceDay, [newDay]);
      if (copiedDays.length === 0) return;
      setActiveDayTab(String(newDay));
      showSuccessToast(
        'Day added',
        `Added Day ${newDay} by copying meals from Day ${sourceDay}.`
      );
    } catch (error) {
      showErrorToast(error, 'Unable to add day');
      throw error;
    }
  };

  const handleReorderMeals = async (day: number, orderedMealOrders: number[]) => {
    if (!draftId) return;
    try {
      const response = await reorderMealPlanMeals(draftId, { day, orderedMealOrders });
      if (!response?.success) {
        throw new Error(response?.message || 'Unable to reorder meal slots');
      }
      await refreshMealPlan({ suppressLoading: true });
      showSuccessToast('Meal slots reordered');
    } catch (error) {
      showErrorToast(error, 'Unable to reorder meal slots');
      throw error;
    }
  };

  const planPractitionerNotes = useMemo(() => {
    const fromPlanNotes = typeof mealPlanData?.planNotes === 'string' ? mealPlanData.planNotes.trim() : '';
    const fromDescription = typeof mealPlanData?.description === 'string' ? mealPlanData.description.trim() : '';
    if (isFinalizedMealPlanStatus(mealPlanData?.status)) return fromDescription || fromPlanNotes;
    return fromPlanNotes || fromDescription;
  }, [mealPlanData?.description, mealPlanData?.planNotes, mealPlanData?.status]);

  const canEditMealPlanNotes = Boolean(draftId);

  const handleSaveMealPlanNotes = useCallback(
    async (request: UpdateMealPlanNotesRequest): Promise<boolean> => {
      if (!draftId || !canEditMealPlanNotes) return false;
      setIsSavingMealPlanNotes(true);
      try {
        const response = await updateMealPlanNotes(draftId, request);
        if (response?.success === false) {
          throw new Error(response?.message || 'Unable to update notes');
        }

        const nextNotes =
          request.notes == null || String(request.notes).trim() === ''
            ? null
            : String(request.notes);

        setMealPlanData((previous) => {
          if (!previous) return previous;
          if (request.level === 'plan') {
            if (isFinalizedMealPlanStatus(previous.status)) {
              return { ...previous, description: nextNotes };
            }
            return { ...previous, planNotes: nextNotes };
          }
          if (request.level === 'day') {
            return {
              ...previous,
              days: previous.days.map((day) =>
                day.day === request.day ? { ...day, notes: nextNotes } : day,
              ),
            };
          }
          if (request.level === 'meal') {
            return {
              ...previous,
              days: previous.days.map((day) => {
                if (day.day !== request.day) return day;
                const meals = { ...(day.meals || {}) };
                const matchKey = Object.keys(meals).find((key) => {
                  const meal = meals[key];
                  const mealName = String(meal?.mealName ?? key).trim();
                  return mealName === request.mealName || key === request.mealName;
                });
                if (!matchKey || !meals[matchKey]) return day;
                meals[matchKey] = { ...meals[matchKey], notes: nextNotes };
                return { ...day, meals };
              }),
            };
          }
          return previous;
        });

        showNotesSavedToast(request.level, nextNotes);
        return true;
      } catch (error) {
        showErrorToast(error, 'Unable to update notes');
        return false;
      } finally {
        setIsSavingMealPlanNotes(false);
      }
    },
    [canEditMealPlanNotes, draftId, setMealPlanData],
  );

  const handleFinalizePlan = async () => {
    if (!draftId || finalizing || isGenerationPending || isFinalizedMealPlanStatus(mealPlanData?.status)) return;
    if (aiDraftSelectionSummary.totalMeals === 0 || aiDraftSelectionSummary.missingSlots.length > 0) {
      const missingCount = aiDraftSelectionSummary.missingSlots.length || aiDraftSelectionSummary.totalMeals;
      const preview = aiDraftSelectionSummary.missingSlots
        .slice(0, 3)
        .map((slot) => `${slot.day ? `Day ${slot.day} ` : ''}${slot.mealName}`.trim())
        .join(', ');
      showWarningToast(
        'Select every meal slot',
        preview
          ? `Select a recipe for all ${missingCount} remaining meal slot${missingCount === 1 ? '' : 's'} before finalising. Missing: ${preview}${missingCount > 3 ? ', ...' : ''}.`
          : 'Select a recipe for every meal slot before finalising this AI meal plan.',
        { duration: 10000 }
      );
      return;
    }

    trackMealPlanFinalizeClicked({
      product_area: 'meal_plans',
      meal_plan_surface: 'ai_draft',
    });

    setFinalizing(true);
    try {
      const fallbackPlanName = `Meal Plan - ${clientDisplayName} - ${new Date().toISOString().slice(0, 10)}`;
      const planName = firstNonEmptyString(
        routeState?.planName,
        mealPlanData?.name,
        mealPlanData?.planName,
        fallbackPlanName
      ) ?? fallbackPlanName;

      const response = await finalizeDraft(draftId, planName);
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to finalize meal plan');
      }

      const status = String(response?.data?.status ?? '').toLowerCase();
      if (isFinalizedMealPlanStatus(status)) {
        showSuccessToast('Meal plan finalized', 'Opening meal plans.');
        navigate(detailsV2MealPlanPath);
        return;
      }

      await refreshMealPlan({ suppressLoading: true });
      showWarningToast(
        'Saved as draft',
        status
          ? `Meal plan is currently ${status}. You can continue editing on this page.`
          : 'Meal plan is still in draft. You can continue editing on this page.'
      );
    } catch (error) {
      const missingDayNumber = extractMissingMealDayNumber(error);
      if (missingDayNumber) {
        const missingDayTab = String(missingDayNumber);
        if (dayTabValues.includes(missingDayTab)) {
          setActiveDayTab(missingDayTab);
        }
        showWarningToast(buildMissingMealDayMessage(missingDayNumber), undefined, { duration: 6000 });
        return;
      }

      showErrorToast(error, 'Finalize failed');
    } finally {
      setFinalizing(false);
    }
  };

  if (loading) {
    if (isGenerationPending) {
      return renderGenerationStepCard();
    }

    return renderLoadingStepCard(
      'Loading meal plan draft...',
      'Please wait while we fetch the latest meal plan data.',
      3
    );
  }

  if (isGenerationPending && !mealPlanData) {
    return renderGenerationStepCard();
  }

  if (!mealPlanData) {
    return (
      <MealPlanEmptyState
        actionLabel="Back to Meal Plans"
        onAction={() => navigate(detailsV2AiMealPlanPath)}
      />
    );
  }

  if (isGenerationPending) {
    const previewDays = mealPlanData.generationPreviewDays ?? [];
    if (!previewDays.length) return renderGenerationStepCard();
    return <>
      <section className="mx-auto max-w-5xl px-5 pt-5" aria-label="Generation progress">
        <h1 className="text-xl font-semibold">Your meal plan is taking shape</h1>
        <p className="mt-2 text-sm text-slate-600">You can preview completed days while the remaining recipes are prepared.</p>
        <progress className="mt-3 w-full" max={100} value={generationProgressValue} aria-label="Meal plan generation progress" />
      </section>
      <GenerationDayPreview days={previewDays} />
    </>;
  }

  if (generationState === 'failed' && !days.length) {
    const failureDescription = generationError?.trim()
      ? `${generationError.trim()} You can return to meal plans and create a new draft.`
      : 'We couldn’t prepare recipes this time. You can return to meal plans and create a new draft.';

    return (
      <MealPlanEmptyState
        title="Your meal plan needs another attempt"
        description={failureDescription}
        actionLabel="Back to Meal Plans"
        onAction={() => navigate(detailsV2AiMealPlanPath)}
      />
    );
  }

  const nutritionSummary = mealPlanData.nutritionSummary;
  const dailyAvg = nutritionSummary?.dailyAverages?.macros;

  const selectedMeals = aiDraftSelectionSummary.selectedMeals;
  const mealPlanAlreadyFinalized = isFinalizedMealPlanStatus(mealPlanData.status);
  const mealPlanChoiceSegments = [
    effectiveDietaryContext.allergies.length > 0
      ? { label: 'Allergies', value: formatDisplayList(effectiveDietaryContext.allergies) }
      : null,
    effectiveDietaryContext.intolerances.length > 0
      ? { label: 'Intolerances', value: formatDisplayList(effectiveDietaryContext.intolerances) }
      : null,
    displayCuisineTypes.length > 0
      ? { label: 'Cuisine Types', value: formatDisplayList(displayCuisineTypes) }
      : null,
    displayPreferences.length > 0
      ? { label: 'Preferences', value: formatDisplayList(displayPreferences) }
      : null,
  ].filter(Boolean);

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
        }}
      >
        <MealPlanDraftHeader
          onBack={() => navigate(detailsV2AiMealPlanPath)}
          clientDisplayName={clientDisplayName}
          clientId={clientId}
          onSaveChanges={handleFinalizePlan}
          saving={finalizing}
          showSaveButton={!mealPlanAlreadyFinalized}
          saveButtonLabel="Finalise Meal Plan"
          activeStep={3}
        />

        <div className="space-y-6 p-4 sm:p-5 lg:px-7 lg:pt-5 lg:pb-7">
          <div data-demo-id="ai-plan-draft-client-summary">
            <MealSuggestionsClientSummaryStrip
              client={clientDetails}
              clientId={String(clientId ?? '')}
              headerAside={
                <>
                  <p className="text-2xl font-semibold leading-none">{selectedMeals}</p>
                  <p className="mt-1 text-xs text-white/85">meals selected</p>
                </>
              }
            />
          </div>

          {(generationError || generationState === 'partial') && <div role="status" className="rounded-xl bg-sky-50 p-4 text-sm text-slate-700">
            <p>{generationState === 'partial'
              ? `Your draft is saved. ${missingGeneratedDays > 0 ? `${missingGeneratedDays} day${missingGeneratedDays === 1 ? ' was' : 's were'} not generated` : ''}${missingGeneratedDays > 0 && emptyMealSlots > 0 ? ' and ' : ''}${emptyMealSlots > 0 ? `${emptyMealSlots} meal${emptyMealSlots === 1 ? '' : 's'} still need${emptyMealSlots === 1 ? 's' : ''} a recipe` : ''}${missingGeneratedDays > 0 || emptyMealSlots > 0 ? '. ' : ''}Keep the recipes you like, regenerate a single meal, or finish everything at once.`
              : 'Your draft is saved. Keep the recipes you like and use saved recipes or generate options for any remaining meals.'}</p>
            {(generationState === 'partial' || generationState === 'failed') && (missingGeneratedDays > 0 || emptyMealSlots > 0) && <button
              type="button"
              className="mt-3 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isResumingGeneration}
              onClick={handleResumeGeneration}
            >{isResumingGeneration ? 'Starting…' : 'Finish remaining meals'}</button>}
          </div>}
          {days.some((day) => day.nutritionReview?.status === 'needs_review' || day.cookingReview?.length) && <section className="rounded-xl border border-amber-200 bg-amber-50 p-4" aria-label="Recipe and nutrition review">
            <p className="font-medium">Review your recipes</p>
            <p className="mt-1 text-sm">These notes apply to your selected recipes. Full-day nutrition is checked once every meal in that day has a selection.</p>
            <ul className="my-3 list-disc pl-5 text-sm">{days.filter((day) => day.nutritionReview?.status === 'needs_review').map((day) => <li key={day.day}>Day {day.day}: {day.nutritionReview?.deviations.join('; ')}</li>)}</ul>
            {days.some((day) => day.cookingReview?.some((recipe) => recipe.issues.some((issue) => issue.code === 'review_unavailable'))) && <p className="my-3 text-sm">Automated cooking review was unavailable for some selected recipes. Please check their methods before sharing.</p>}
            {days.map((day) => day.cookingReview?.some((recipe) => recipe.issues.some((issue) => issue.code !== 'review_unavailable')) ? <details key={`cooking-${day.day}`} className="my-3 text-sm">
              <summary className="cursor-pointer font-medium">Day {day.day}: cooking and practicality notes</summary>
              <ul className="mt-2 list-disc space-y-2 pl-5">{day.cookingReview.filter((recipe) => recipe.issues.some((issue) => issue.code !== 'review_unavailable')).map((recipe) => <li key={recipe.recipeId}><strong>{recipe.title}</strong>: {recipe.issues.filter((issue) => issue.code !== 'review_unavailable').map((issue) => issue.message).join(' ')}</li>)}</ul>
            </details> : null)}
          </section>}
          <div data-demo-id="ai-plan-draft-days">
            <MealPlanDaysTabs
              days={days}
              activeDayTab={activeDayTab}
              onActiveDayTabChange={setActiveDayTab}
              smartEditDisabled={activeDaySmartEditLimitReached}
              smartEditLimitReached={activeDaySmartEditLimitReached}
              smartEditLimit={activeDaySmartEditDailyLimit}
              smartEditRetryAt={activeDaySmartEditRetryAt}
              regenerateDisabled={activeDayRegenerateLimitReached}
              getMealKey={getMealKey}
              alternateRecipesMap={alternateRecipesMap}
              onViewDetails={handleViewDetails}
              onSwapRecipe={handleSwapRecipe}
              swappingMeal={effectiveSwappingMeal}
              onSelectAlternateRecipe={handleSelectAlternateRecipe}
              onClearMealSelection={handleClearMealSelection}
              onOpenSmartEdit={handleOpenSmartEdit}
              onCopyExistingDays={handleCopyExistingDays}
              onDuplicateToNextDay={handleDuplicateToNextDay}
              onAddCopiedDay={handleAddCopiedDay}
              onApplyToAllDays={handleApplyToAllDays}
              onReorderMeals={handleReorderMeals}
              planNotes={planPractitionerNotes}
              canEditNotes={canEditMealPlanNotes}
              isSavingNotes={isSavingMealPlanNotes}
              onSaveNotes={handleSaveMealPlanNotes}
              preferredChoicesSummary={
                mealPlanChoiceSegments.length > 0 ? (
                  <section className="rounded-xl border border-cyan-100 bg-cyan-50/70 px-4 py-3 text-sm font-medium text-slate-700">
                    <span>Your preferred choices for this meal plan - </span>
                    {mealPlanChoiceSegments.map((segment, index) => (
                      <React.Fragment key={segment.label}>
                        {index > 0 ? <span>; </span> : null}
                        <strong className="font-semibold text-slate-900">{segment.label}:</strong>{' '}
                        <span>{segment.value}</span>
                      </React.Fragment>
                    ))}
                  </section>
                ) : null
              }
            />
          </div>

          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              className="text-sm font-medium text-slate-600 transition-colors hover:text-slate-800"
              onClick={() => navigate(detailsV2AiMealPlanPath)}
            >
              Cancel
            </button>
            {!mealPlanAlreadyFinalized ? (
              <MuiButton
                data-demo-id="ai-plan-draft-finalise"
                type="button"
                variant="contained"
                startIcon={<SaveRoundedIcon sx={{ fontSize: 14 }} />}
                onClick={handleFinalizePlan}
                disabled={finalizing}
                sx={{
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '20px',
                  height: '38px',
                  px: '14px',
                  borderRadius: '8px',
                  bgcolor: '#20AEDC',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#20AEDC',
                    boxShadow: 'none',
                  },
                }}
              >
                {finalizing ? 'Saving...' : 'Finalise Meal Plan'}
              </MuiButton>
            ) : null}
          </div>

          <SmartEditModal
            open={smartEditOpen}
            onOpenChange={handleSmartEditOpenChange}
            meal={smartEditTarget}
            prompt={smartEditPrompt}
            onPromptChange={handleSmartEditPromptChange}
            onPresetSelect={handleSmartEditPresetSelect}
            onSendPrompt={handleSmartEditSendPrompt}
            suggestions={smartEditSuggestions}
            selectedSuggestionId={smartEditSelectedSuggestionId}
            onSelectSuggestion={handleApplySmartEditSuggestion}
            inProgressNotice={smartEditProgressNotice}
            generating={smartEditGenerating}
            applying={smartEditApplying}
            attemptsLeft={smartEditAttemptsLeft}
            attemptsUsed={smartEditAttemptsUsed}
            dailyLimit={smartEditDailyLimit}
            generateDisabled={
              isShufflePreparingAlternates ||
              isSmartEditPreparingSuggestions ||
              Boolean(smartEditProgressNotice)
            }
          />
        </div>
      </Paper>
    </Box>
  );
};

export default AIMealPlanDraftPage;
